import { __extends } from "tslib";
import { avm1CompilerEnabled, avm1ErrorsEnabled, avm1TimeoutDisabled, avm1TraceEnabled, avm1WarningsEnabled, } from './settings';
import { AVM1Context } from './context';
import { ActionsDataAnalyzer } from './analyze';
import { ActionsDataParser, ParsedPushConstantAction, ParsedPushRegisterAction } from './parser';
import { ActionsDataCompiler } from './baseline';
import { alCoerceString, alDefineObjectProperties, alForEachProperty, alIsArray, alIsFunction, alIsName, alNewObject, alToBoolean, alToInt32, alToNumber, alToObject, alToPrimitive, alToString, AVM1EvalFunction, AVM1NativeFunction, bToRuntimeBool } from './runtime';
import { AVM1Globals, AVM1NativeActions } from './lib/AVM1Globals';
import { Telemetry, isNullOrUndefined, Debug, release, assert } from '@awayfl/swf-loader';
import { hasAwayJSAdaptee } from './lib/AVM1Utils';
import { AVM1MovieClip } from './lib/AVM1MovieClip';
import { AVM1ArrayNative } from './natives';
import { AVM1Object } from './runtime/AVM1Object';
import { AVM1Function } from './runtime/AVM1Function';
import { AVM1PropertyDescriptor } from './runtime/AVM1PropertyDescriptor';
import { MovieClipProperties } from './interpreter/MovieClipProperties';
import { TextField, FrameScriptManager } from '@awayjs/scene';
var noVarGetDebug = true;
export var Debugger = {
    pause: false,
    breakpoints: {}
};
function avm1Warn(message, arg1, arg2, arg3, arg4) {
    if (avm1ErrorsEnabled.value) {
        try {
            throw new Error(message); // using throw as a way to break in browsers debugger
        }
        catch (e) { /* ignoring since handled */
        }
    }
    if (avm1WarningsEnabled.value) {
        /* eslint-disable-next-line */
        Debug.warning.apply(console, arguments);
    }
}
export var MAX_AVM1_HANG_TIMEOUT = 1000;
export var CHECK_AVM1_HANG_EVERY = 1000;
var MAX_AVM1_ERRORS_LIMIT = 1000;
var MAX_AVM1_STACK_LIMIT = 256;
export var AVM1ScopeListItemFlags;
(function (AVM1ScopeListItemFlags) {
    AVM1ScopeListItemFlags[AVM1ScopeListItemFlags["DEFAULT"] = 0] = "DEFAULT";
    AVM1ScopeListItemFlags[AVM1ScopeListItemFlags["TARGET"] = 1] = "TARGET";
    AVM1ScopeListItemFlags[AVM1ScopeListItemFlags["REPLACE_TARGET"] = 2] = "REPLACE_TARGET";
})(AVM1ScopeListItemFlags || (AVM1ScopeListItemFlags = {}));
var AVM1ScopeListItem = /** @class */ (function () {
    function AVM1ScopeListItem(scope, previousScopeItem) {
        this.scope = scope;
        this.previousScopeItem = previousScopeItem;
        this.flags = AVM1ScopeListItemFlags.DEFAULT;
    }
    return AVM1ScopeListItem;
}());
export { AVM1ScopeListItem };
// Similar to function scope, mostly for 'this'.
var GlobalPropertiesScope = /** @class */ (function (_super) {
    __extends(GlobalPropertiesScope, _super);
    function GlobalPropertiesScope(context, thisArg) {
        var _this = _super.call(this, context) || this;
        _this.alSetOwnProperty('this', new AVM1PropertyDescriptor(64 /* DATA */ |
            1 /* DONT_ENUM */ |
            2 /* DONT_DELETE */ |
            4 /* READ_ONLY */, thisArg));
        _this.alSetOwnProperty('_global', new AVM1PropertyDescriptor(64 /* DATA */ |
            1 /* DONT_ENUM */ |
            2 /* DONT_DELETE */ |
            4 /* READ_ONLY */, context.globals));
        return _this;
    }
    return GlobalPropertiesScope;
}(AVM1Object));
export { GlobalPropertiesScope };
var AVM1CallFrame = /** @class */ (function () {
    function AVM1CallFrame(previousFrame, currentThis, fn, args, ectx) {
        this.previousFrame = previousFrame;
        this.currentThis = currentThis;
        this.fn = fn;
        this.args = args;
        this.ectx = ectx;
        this.inSequence = !previousFrame ? false :
            (previousFrame.calleeThis === currentThis && previousFrame.calleeFn === fn);
        this.resetCallee();
    }
    AVM1CallFrame.prototype.setCallee = function (thisArg, superArg, fn, args) {
        this.calleeThis = thisArg;
        this.calleeSuper = superArg;
        this.calleeFn = fn;
        if (!release) {
            this.calleeArgs = args;
        }
    };
    AVM1CallFrame.prototype.resetCallee = function () {
        this.calleeThis = null;
        this.calleeSuper = null;
        this.calleeFn = null;
    };
    return AVM1CallFrame;
}());
export { AVM1CallFrame };
var AVM1RuntimeUtilsImpl = /** @class */ (function () {
    function AVM1RuntimeUtilsImpl(context) {
        this._context = context;
    }
    AVM1RuntimeUtilsImpl.prototype.hasProperty = function (obj, name) {
        return as2HasProperty(this._context, obj, name);
    };
    AVM1RuntimeUtilsImpl.prototype.getProperty = function (obj, name) {
        return as2GetProperty(this._context, obj, name);
    };
    AVM1RuntimeUtilsImpl.prototype.setProperty = function (obj, name, value) {
        return as2SetProperty(this._context, obj, name, value);
    };
    AVM1RuntimeUtilsImpl.prototype.warn = function (msg) {
        /* eslint-disable-next-line */
        avm1Warn.apply(null, arguments);
    };
    return AVM1RuntimeUtilsImpl;
}());
var AVM1ContextImpl = /** @class */ (function (_super) {
    __extends(AVM1ContextImpl, _super);
    function AVM1ContextImpl(swfVersion) {
        var _this = _super.call(this, swfVersion) || this;
        _this.globals = AVM1Globals.createGlobalsObject(_this);
        _this.actions = new AVM1NativeActions(_this);
        _this.initialScope = new AVM1ScopeListItem(_this.globals, null);
        _this.utils = new AVM1RuntimeUtilsImpl(_this);
        _this.isActive = false;
        _this.executionProhibited = false;
        _this.actionTracer = avm1TraceEnabled.value ? new ActionTracer() : null;
        _this.abortExecutionAt = 0;
        _this.stackDepth = 0;
        _this.frame = null;
        _this.isTryCatchListening = false;
        _this.errorsIgnored = 0;
        _this.deferScriptExecution = true;
        return _this;
    }
    AVM1ContextImpl.prototype._getExecutionContext = function () {
        // We probably entering this function from some native function,
        // so faking execution context. Let's reuse last created context.
        return this.frame.ectx;
    };
    AVM1ContextImpl.prototype.resolveTarget = function (target) {
        var ectx = this._getExecutionContext();
        return avm1ResolveTarget(ectx, target, true);
    };
    AVM1ContextImpl.prototype.resolveRoot = function () {
        var ectx = this._getExecutionContext();
        return avm1ResolveRoot(ectx);
    };
    AVM1ContextImpl.prototype.checkTimeout = function () {
        if (Date.now() >= this.abortExecutionAt) {
            //80pro - this fires even for short scripts:
            //throw new AVM1CriticalError('long running script -- AVM1 instruction hang timeout');
        }
    };
    AVM1ContextImpl.prototype.pushCallFrame = function (thisArg, fn, args, ectx) {
        var nextFrame = new AVM1CallFrame(this.frame, thisArg, fn, args, ectx);
        this.frame = nextFrame;
        return nextFrame;
    };
    AVM1ContextImpl.prototype.popCallFrame = function () {
        var previousFrame = this.frame.previousFrame;
        this.frame = previousFrame;
        return previousFrame;
    };
    AVM1ContextImpl.prototype.executeActions = function (actionsData, scopeObj) {
        if (this.executionProhibited) {
            return; // no more avm1 for this context
        }
        var savedIsActive = this.isActive;
        if (!savedIsActive) {
            this.isActive = true;
            this.abortExecutionAt = avm1TimeoutDisabled.value ?
                Number.MAX_VALUE : Date.now() + MAX_AVM1_HANG_TIMEOUT;
            this.errorsIgnored = 0;
        }
        var caughtError;
        //console.log("executeActions", scopeObj.aCount);
        try {
            executeActionsData(this, actionsData, scopeObj);
        }
        catch (e) {
            caughtError = e;
        }
        this.isActive = savedIsActive;
        if (caughtError) {
            // Note: this doesn't use `finally` because that's a no-go for performance.
            console.error('error in framescripts', caughtError);
            //throw caughtError;
        }
    };
    AVM1ContextImpl.prototype.executeFunction = function (fn, thisArg, args) {
        if (this.executionProhibited) {
            return; // no more avm1 for this context
        }
        var savedIsActive = this.isActive;
        if (!savedIsActive) {
            this.isActive = true;
            this.abortExecutionAt = avm1TimeoutDisabled.value ?
                Number.MAX_VALUE : Date.now() + MAX_AVM1_HANG_TIMEOUT;
            this.errorsIgnored = 0;
        }
        var caughtError;
        var result;
        try {
            result = fn.alCall(thisArg, args);
        }
        catch (e) {
            caughtError = e;
        }
        this.isActive = savedIsActive;
        if (caughtError) {
            // Note: this doesn't use `finally` because that's a no-go for performance.
            throw caughtError;
        }
        return result;
    };
    return AVM1ContextImpl;
}(AVM1Context));
export { AVM1ContextImpl };
AVM1Context.create = function (swfVersion) {
    return new AVM1ContextImpl(swfVersion);
};
var AVM1Error = /** @class */ (function () {
    function AVM1Error(error) {
        this.error = error;
    }
    return AVM1Error;
}());
var AVM1CriticalError = /** @class */ (function (_super) {
    __extends(AVM1CriticalError, _super);
    function AVM1CriticalError(message, error) {
        var _this = _super.call(this, message) || this;
        _this.error = error;
        return _this;
    }
    return AVM1CriticalError;
}(Error));
function isAVM1MovieClip(obj) {
    return typeof obj === 'object' && obj &&
        obj instanceof AVM1MovieClip;
}
function as2GetType(v) {
    if (v === null) {
        return 'null';
    }
    var type = typeof v;
    if (typeof v === 'object') {
        if (v instanceof AVM1MovieClip) {
            return 'movieclip';
        }
        if (v instanceof AVM1Function) {
            return 'function';
        }
    }
    return type;
}
/**
 * Performs "less" comparison of two arugments.
 * @returns {boolean} Returns true if x is less than y, otherwise false
 */
function as2Compare(context, x, y) {
    var x2 = alToPrimitive(context, x);
    var y2 = alToPrimitive(context, y);
    if (typeof x2 === 'string' && typeof y2 === 'string') {
        var xs = alToString(context, x2), ys = alToString(context, y2);
        return xs < ys;
    }
    else {
        var xn = alToNumber(context, x2), yn = alToNumber(context, y2);
        return isNaN(xn) || isNaN(yn) ? undefined : xn < yn;
    }
}
/**
 * Performs equality comparison of two arugments. The equality comparison
 * algorithm from EcmaScript 3, Section 11.9.3 is applied.
 * @see http://ecma-international.org/publications/files/ECMA-ST-ARCH/ECMA-262,%203rd%20edition,%20December%201999.pdf#page=67
 * @returns {boolean} Coerces x and y to the same type and returns true if they're equal, false otherwise.
 */
function as2Equals(context, x, y) {
    // Spec steps 1 through 13 can be condensed to ...
    if (typeof x === typeof y) {
        if (typeof x === 'number') {
            // Calculate the difference.
            var ma = Math.abs(x), mb = Math.abs(y);
            var larges = ma > mb ? ma : mb;
            var eps = (1e-6) * larges;
            return Math.abs(x - y) <= eps;
        }
        return x === y;
    }
    // Spec steps 14 and 15.
    if (x == null && y == null) {
        return true;
    }
    /*
    if (typeof x === 'undefined' && typeof y === 'string' && y=="") {
        // Unfolding the recursion for `as2Equals(context, x, alToNumber(y))`
        return true; // in AVM1, ToNumber('') === NaN
    }
    if (typeof y === 'undefined' && typeof x === 'string' && x=="") {
        // Unfolding the recursion for `as2Equals(context, x, alToNumber(y))`
        return true; // in AVM1, ToNumber('') === NaN
    }
    */
    // Spec steps 16 and 17.
    if (typeof x === 'number' && typeof y === 'string') {
        // Unfolding the recursion for `as2Equals(context, x, alToNumber(y))`
        return y === '' ? false : x === +y; // in AVM1, ToNumber('') === NaN
    }
    if (typeof x === 'string' && typeof y === 'number') {
        // Unfolding the recursion for `as2Equals(context, alToNumber(x), y)`
        return x === '' ? false : +x === y; // in AVM1, ToNumber('') === NaN
    }
    // Spec step 18.
    if (typeof x === 'boolean') {
        // Unfolding the recursion for `as2Equals(context, alToNumber(x), y)`
        x = +x; // typeof x === 'number'
        if (typeof y === 'number' || typeof y === 'string') {
            return y === '' ? false : x === +y;
        }
        // Fall through for typeof y === 'object', 'boolean', 'undefined' cases
    }
    // Spec step 19.
    if (typeof y === 'boolean') {
        // Unfolding the recursion for `as2Equals(context, x, alToNumber(y))`
        y = +y; // typeof y === 'number'
        if (typeof x === 'number' || typeof x === 'string') {
            return x === '' ? false : +x === y;
        }
        // Fall through for typeof x === 'object', 'undefined' cases
    }
    // Spec step 20.
    if ((typeof x === 'number' || typeof x === 'string') &&
        typeof y === 'object' && y !== null) {
        y = alToPrimitive(context, y);
        if (typeof y === 'object') {
            return false; // avoiding infinite recursion
        }
        return as2Equals(context, x, y);
    }
    // Spec step 21.
    if (typeof x === 'object' && x !== null &&
        (typeof y === 'number' || typeof y === 'string')) {
        x = alToPrimitive(context, x);
        if (typeof x === 'object') {
            return false; // avoiding infinite recursion
        }
        return as2Equals(context, x, y);
    }
    return false;
}
function as2InstanceOf(obj, constructor) {
    // TODO refactor this -- quick and dirty hack for now
    if (isNullOrUndefined(obj) || isNullOrUndefined(constructor)) {
        return false;
    }
    // if (constructor === ASString) {
    // 	return typeof obj === 'string';
    // } else if (constructor === ASNumber) {
    // 	return typeof obj === 'number';
    // } else if (constructor === ASBoolean) {
    // 	return typeof obj === 'boolean';
    // } else if (constructor === ASArray) {
    // 	return Array.isArray(obj);
    // } else if (constructor === ASFunction) {
    // 	return typeof obj === 'function';
    // } else if (constructor === ASObject) {
    // 	return typeof obj === 'object';
    // }
    var baseProto = constructor.alGetPrototypeProperty();
    if (!baseProto) {
        return false;
    }
    var proto = obj;
    while (proto) {
        if (proto === baseProto) {
            return true; // found the type if the chain
        }
        proto = proto.alPrototype;
    }
    // TODO interface check
    return false;
}
function as2HasProperty(context, obj, name) {
    var avm1Obj = alToObject(context, obj);
    name = context.normalizeName(name);
    return avm1Obj.alHasProperty(name);
}
function as2GetProperty(context, obj, name) {
    var avm1Obj = alToObject(context, obj);
    if (!avm1Obj)
        return undefined;
    var value = avm1Obj.alGet(name);
    //if(typeof name==="string" && name.toLowerCase()=="ox"){
    //	console.log("get ox", avm1Obj.adaptee.id, avm1Obj.adaptee.name, value);
    //}
    return value;
}
function as2SetProperty(context, obj, name, value) {
    var avm1Obj = alToObject(context, obj);
    if (!avm1Obj)
        return;
    //if(typeof name==="string" && name.toLowerCase()=="ox"){
    //	console.log("set ox", avm1Obj.adaptee.id, avm1Obj.adaptee.name, value);
    //}
    if (name == '__proto__') {
        if (value) {
            var allKeys = value.alGetKeys();
            for (var i = 0; i < allKeys.length; i++) {
                var key = allKeys[i];
                if (key != '') {
                    avm1Obj.alPut(key, value.alGet(key));
                    as2SyncEvents(context, key, avm1Obj);
                }
            }
            avm1Obj.protoTypeChanged = !(value instanceof AVM1MovieClip);
        }
    }
    else {
        avm1Obj.alPut(name, value);
        if (avm1Obj.adaptee) {
            // todo: this might not be the best way
            // the goal is to not call as2SyncEvents when avm1Obj is a prototype object
            // but idk how to identify if avm1Obj is prototype.
            // for now i just use the adaptee to check, because a prototype should not have adaptee set
            as2SyncEvents(context, name, avm1Obj);
        }
    }
}
function as2DeleteProperty(context, obj, name) {
    var avm1Obj = alToObject(context, obj);
    name = context.normalizeName(name);
    var result = avm1Obj.alDeleteProperty(name);
    as2SyncEvents(context, name, avm1Obj);
    return result;
}
function as2SyncEvents(context, name, avm1Obj) {
    if (typeof name === 'undefined')
        return;
    name = alCoerceString(context, name);
    name = context.normalizeName(name);
    if (name[0] !== 'o' || name[1] !== 'n') { // TODO check case?
        return;
    }
    if (avm1Obj && avm1Obj.updateEventByPropName)
        avm1Obj.updateEventByPropName(name);
    // Maybe an event property, trying to broadcast change.
    //(<AVM1ContextImpl>context).broadcastEventPropertyChange(name);
}
function as2CastError(ex) {
    if (typeof InternalError !== 'undefined' &&
        ex instanceof InternalError && ex.message === 'too much recursion') {
        // HACK converting too much recursion into AVM1CriticalError
        //console.log('long running script -- AVM1 recursion limit is reached');
        return new AVM1CriticalError('long running script -- AVM1 recursion limit is reached');
    }
    return ex;
}
function as2Construct(ctor, args) {
    var result;
    if (alIsFunction(ctor)) {
        result = ctor.alConstruct(args);
    }
    else {
        // AVM1 simply ignores attempts to invoke non-methods.
        return undefined;
    }
    return result;
}
function as2Enumerate(obj, fn, thisArg) {
    // todo: better just whitelist "typeof === object" instead of blacklisting ?
    if (typeof obj === 'boolean' || typeof obj === 'string' || typeof obj === 'number') {
        return;
    }
    alForEachProperty(obj, function (name) {
        var _a, _b;
        if (typeof name == 'string' && name.indexOf('_internal_TF') != -1)
            return;
        var avmObj = obj.alGet(name);
        if (((_b = (_a = avmObj) === null || _a === void 0 ? void 0 : _a.adaptee) === null || _b === void 0 ? void 0 : _b.isAsset(TextField)) && avmObj.adaptee.isStatic)
            return;
        fn.call(thisArg, name);
    }, thisArg);
    /*
    let i = props.length;
    let avmObj = null;
    while (i > 0) {
        i--;
        fn.call(thisArg, props[i]);
    }*/
}
function avm1FindSuperPropertyOwner(context, frame, propertyName) {
    if (context.swfVersion < 6) {
        return null;
    }
    var proto = (frame.inSequence && frame.previousFrame.calleeSuper);
    if (!proto) {
        // Finding first object in prototype chain link that has the property.
        proto = frame.currentThis;
        while (proto && !proto.alHasOwnProperty(propertyName)) {
            proto = proto.alPrototype;
        }
        if (!proto) {
            return null;
        }
    }
    // Skipping one chain link
    proto = proto.alPrototype;
    return proto;
}
var DEFAULT_REGISTER_COUNT = 4;
function executeActionsData(context, actionsData, scope) {
    var actionTracer = context.actionTracer;
    var globalPropertiesScopeList = new AVM1ScopeListItem(new GlobalPropertiesScope(context, scope), context.initialScope);
    var scopeList = new AVM1ScopeListItem(scope, globalPropertiesScopeList);
    scopeList.flags |= AVM1ScopeListItemFlags.TARGET;
    var caughtError;
    release || (actionTracer && actionTracer.message('ActionScript Execution Starts'));
    release || (actionTracer && actionTracer.indent());
    var ectx = ExecutionContext.create(context, scopeList, [], DEFAULT_REGISTER_COUNT);
    context.pushCallFrame(scope, null, null, ectx);
    try {
        interpretActionsData(ectx, actionsData);
    }
    catch (e) {
        caughtError = as2CastError(e);
    }
    ectx.dispose();
    if (caughtError instanceof AVM1CriticalError) {
        context.executionProhibited = true;
        console.error('Disabling AVM1 execution');
    }
    context.popCallFrame();
    release || (actionTracer && actionTracer.unindent());
    release || (actionTracer && actionTracer.message('ActionScript Execution Stops'));
    if (caughtError) {
        // Note: this doesn't use `finally` because that's a no-go for performance.
        throw caughtError; // TODO shall we just ignore it?
    }
}
function createBuiltinType(context, cls, args) {
    var builtins = context.builtins;
    var obj = undefined;
    if (cls === builtins.Array || cls === builtins.Object ||
        cls === builtins.Date || cls === builtins.String ||
        cls === builtins.Function) {
        obj = cls.alConstruct(args);
    }
    if (cls === builtins.Boolean || cls === builtins.Number) {
        obj = cls.alConstruct(args).value;
    }
    if (obj instanceof AVM1Object) {
        var desc = new AVM1PropertyDescriptor(64 /* DATA */ | 1 /* DONT_ENUM */, cls);
        obj.alSetOwnProperty('__constructor__', desc);
    }
    return obj;
}
var AVM1SuperWrapper = /** @class */ (function (_super) {
    __extends(AVM1SuperWrapper, _super);
    function AVM1SuperWrapper(context, callFrame) {
        var _this = _super.call(this, context) || this;
        _this.callFrame = callFrame;
        _this.alPrototype = context.builtins.Object.alGetPrototypeProperty();
        return _this;
    }
    return AVM1SuperWrapper;
}(AVM1Object));
var AVM1Arguments = /** @class */ (function (_super) {
    __extends(AVM1Arguments, _super);
    function AVM1Arguments(context, args, callee, caller) {
        var _this = _super.call(this, context, args) || this;
        alDefineObjectProperties(_this, {
            callee: {
                value: callee
            },
            caller: {
                value: caller
            }
        });
        return _this;
    }
    return AVM1Arguments;
}(AVM1ArrayNative));
var ExecutionContext = /** @class */ (function () {
    function ExecutionContext(context, scopeList, constantPool, registerCount) {
        this.framescriptmanager = FrameScriptManager;
        this.context = context;
        this.actions = context.actions;
        this.isSwfVersion5 = context.swfVersion >= 5;
        this.isSwfVersion7 = context.swfVersion >= 7;
        this.registers = [];
        this.stack = [];
        this.frame = null;
        this.recoveringFromError = false;
        this.isEndOfActions = false;
        this.reset(scopeList, constantPool, registerCount);
    }
    ExecutionContext.alInitStatic = function () {
        this.cache = [];
    };
    ExecutionContext.prototype.reset = function (scopeList, constantPool, registerCount) {
        this.scopeList = scopeList;
        this.constantPool = constantPool;
        this.registers.length = registerCount;
    };
    ExecutionContext.prototype.clean = function () {
        this.scopeList = null;
        this.constantPool = null;
        this.registers.length = 0;
        this.stack.length = 0;
        this.frame = null;
        this.recoveringFromError = false;
        this.isEndOfActions = false;
    };
    ExecutionContext.prototype.pushScope = function (newScopeList) {
        var newContext = Object.create(this);
        newContext.stack = [];
        if (!isNullOrUndefined(newScopeList)) {
            newContext.scopeList = newScopeList;
        }
        return newContext;
    };
    ExecutionContext.prototype.dispose = function () {
        this.clean();
        var state = this.context.getStaticState(ExecutionContext);
        if (state.cache.length < ExecutionContext.MAX_CACHED_EXECUTIONCONTEXTS) {
            state.cache.push(this);
        }
    };
    /* eslint-disable-next-line */
    ExecutionContext.create = function (context, scopeList, constantPool, registerCount) {
        var state = context.getStaticState(ExecutionContext);
        var ectx;
        if (state.cache.length > 0) {
            ectx = state.cache.pop();
            ectx.reset(scopeList, constantPool, registerCount);
        }
        else {
            ectx = new ExecutionContext(context, scopeList, constantPool, registerCount);
        }
        return ectx;
    };
    ExecutionContext.MAX_CACHED_EXECUTIONCONTEXTS = 20;
    return ExecutionContext;
}());
export { ExecutionContext };
/**
 * Interpreted function closure.
 */
var AVM1InterpreterScope = /** @class */ (function (_super) {
    __extends(AVM1InterpreterScope, _super);
    function AVM1InterpreterScope(context) {
        var _this = _super.call(this, context) || this;
        _this.alPut('toString', new AVM1NativeFunction(context, _this._toString));
        return _this;
    }
    AVM1InterpreterScope.prototype._toString = function () {
        // It shall return 'this'
        return this;
    };
    return AVM1InterpreterScope;
}(AVM1Object));
var AVM1InterpretedFunction = /** @class */ (function (_super) {
    __extends(AVM1InterpretedFunction, _super);
    function AVM1InterpretedFunction(context, ectx, actionsData, functionName, parametersNames, registersCount, registersAllocation, suppressArguments) {
        var _this = _super.call(this, context) || this;
        _this.functionName = functionName;
        _this.actionsData = actionsData;
        _this.parametersNames = parametersNames;
        _this.registersAllocation = registersAllocation;
        _this.suppressArguments = suppressArguments;
        _this.scopeList = ectx.scopeList;
        _this.constantPool = ectx.constantPool;
        var skipArguments = null;
        var registersAllocationCount = !registersAllocation ? 0 : registersAllocation.length;
        for (var i = 0; i < registersAllocationCount; i++) {
            var registerAllocation = registersAllocation[i];
            if (registerAllocation &&
                registerAllocation.type === 1 /* Argument */) {
                if (!skipArguments) {
                    skipArguments = [];
                }
                skipArguments[registersAllocation[i].index] = true;
            }
        }
        _this.skipArguments = skipArguments;
        var registersLength = Math.min(registersCount, 255); // max allowed for DefineFunction2
        registersLength = Math.max(registersLength, registersAllocationCount + 1);
        _this.registersLength = registersLength;
        return _this;
    }
    AVM1InterpretedFunction.prototype.alCall = function (thisArg, args) {
        var _a, _b;
        var currentContext = this.context;
        if (currentContext.executionProhibited) {
            return; // no more avm1 execution, ever
        }
        var newScope = new AVM1InterpreterScope(currentContext);
        var newScopeList = new AVM1ScopeListItem(newScope, this.scopeList);
        var oldScope = this.scopeList.scope;
        //thisArg = thisArg || oldScope; // REDUX no isGlobalObject check?
        args = args || [];
        var ectx = ExecutionContext.create(currentContext, newScopeList, this.constantPool, this.registersLength);
        var caller = currentContext.frame ? currentContext.frame.fn : undefined;
        var frame = currentContext.pushCallFrame(thisArg, this, args, ectx);
        var supperWrapper;
        var suppressArguments = this.suppressArguments;
        if (!(suppressArguments & 4 /* Arguments */)) {
            newScope.alPut('arguments', new AVM1Arguments(currentContext, args, this, caller));
        }
        if (!(suppressArguments & 2 /* This */)) {
            newScope.alPut('this', thisArg);
        }
        if (!(suppressArguments & 8 /* Super */)) {
            supperWrapper = new AVM1SuperWrapper(currentContext, frame);
            newScope.alPut('super', supperWrapper);
        }
        var i;
        var registers = ectx.registers;
        var registersAllocation = this.registersAllocation;
        var registersAllocationCount = !registersAllocation ? 0 : registersAllocation.length;
        for (i = 0; i < registersAllocationCount; i++) {
            var registerAllocation = registersAllocation[i];
            if (registerAllocation) {
                switch (registerAllocation.type) {
                    case 1 /* Argument */:
                        registers[i] = args[registerAllocation.index];
                        break;
                    case 2 /* This */:
                        registers[i] = thisArg;
                        break;
                    case 4 /* Arguments */:
                        registers[i] = new AVM1Arguments(currentContext, args, this, caller);
                        break;
                    case 8 /* Super */:
                        supperWrapper = supperWrapper || new AVM1SuperWrapper(currentContext, frame);
                        registers[i] = supperWrapper;
                        break;
                    case 16 /* Global */:
                        registers[i] = currentContext.globals;
                        break;
                    case 32 /* Parent */: {
                        var parentObj = null;
                        if (oldScope) {
                            parentObj = oldScope.alGet('_parent');
                            if (!parentObj) {
                                parentObj = oldScope.alGet('this');
                                if (parentObj) {
                                    parentObj = parentObj.alGet('_parent');
                                }
                            }
                        }
                        if (!parentObj) {
                            // if the _parent was not set from oldScope, we get it from thisArg
                            parentObj = thisArg;
                            if (parentObj) {
                                parentObj = parentObj.alGet('_parent');
                            }
                            //  if this is a onEnter, and the _parent was not set from oldScope,
                            //  we need to go up another parent if possible
                            if (parentObj && this.isOnEnter && parentObj.alGet('_parent')) {
                                parentObj = parentObj.alGet('_parent');
                            }
                            // for setInterval: if its still not has a parent found
                            //  we look back at the previous-scopes until we find a scope that can provide a _parent
                            if (!parentObj) {
                                if ((_b = (_a = this.scopeList) === null || _a === void 0 ? void 0 : _a.previousScopeItem) === null || _b === void 0 ? void 0 : _b.scope) {
                                    var currentScope = this.scopeList.previousScopeItem;
                                    while (currentScope) {
                                        if (currentScope.scope && currentScope.scope instanceof AVM1MovieClip) {
                                            parentObj = currentScope.scope;
                                        }
                                        else if (currentScope.scope) {
                                            parentObj = currentScope.scope.alGet('this');
                                        }
                                        if (parentObj) {
                                            parentObj = parentObj.alGet('_parent');
                                        }
                                        if (currentScope.previousScopeItem)
                                            currentScope = currentScope.previousScopeItem;
                                        else
                                            currentScope = null;
                                    }
                                }
                            }
                        }
                        /*if(this.isOnEnter){
                            console.log("prepare on enter");
                            console.log("oldScope parent", oldScope.alGet("_parent"));
                            console.log("oldScope this", oldScope.alGet("this"));
                            console.log("newscope this", newScope.alGet("_parent"));
                            console.log("thisArg", thisArg);
                        }*/
                        if (parentObj) {
                            registers[i] = parentObj;
                        }
                        else {
                            //console.log("_parent not defined");
                        }
                        break;
                    }
                    case 64 /* Root */:
                        registers[i] = avm1ResolveRoot(ectx);
                        break;
                }
            }
        }
        var parametersNames = this.parametersNames;
        var skipArguments = this.skipArguments;
        for (i = 0; i < args.length || i < parametersNames.length; i++) {
            if (skipArguments && skipArguments[i]) {
                continue;
            }
            newScope.alPut(parametersNames[i], args[i]);
        }
        var result;
        var caughtError;
        var actionTracer = currentContext.actionTracer;
        var actionsData = this.actionsData;
        release || (actionTracer && actionTracer.indent());
        if (++currentContext.stackDepth >= MAX_AVM1_STACK_LIMIT) {
            throw new AVM1CriticalError('long running script -- AVM1 recursion limit is reached');
        }
        try {
            result = interpretActionsData(ectx, actionsData);
        }
        catch (e) {
            caughtError = e;
        }
        currentContext.stackDepth--;
        currentContext.popCallFrame();
        ectx.dispose();
        release || (actionTracer && actionTracer.unindent());
        if (caughtError) {
            // Note: this doesn't use `finally` because that's a no-go for performance.
            throw caughtError;
        }
        return result;
    };
    return AVM1InterpretedFunction;
}(AVM1EvalFunction));
export { AVM1InterpretedFunction };
function fixArgsCount(numArgs /* int */, maxAmount) {
    if (isNaN(numArgs) || numArgs < 0) {
        avm1Warn('Invalid amount of arguments: ' + numArgs);
        return 0;
    }
    numArgs |= 0;
    if (numArgs > maxAmount) {
        avm1Warn('Truncating amount of arguments: from ' + numArgs + ' to ' + maxAmount);
        return maxAmount;
    }
    return numArgs;
}
function avm1ReadFunctionArgs(stack) {
    var numArgs = +stack.pop();
    numArgs = fixArgsCount(numArgs, stack.length);
    var args = [];
    for (var i = 0; i < numArgs; i++) {
        args.push(stack.pop());
    }
    return args;
}
function avm1SetTarget(ectx, targetPath) {
    var newTarget = null;
    if (targetPath) {
        if (typeof targetPath === 'string') {
            while (targetPath.length && targetPath[targetPath.length - 1] == '.') {
                targetPath = targetPath.substring(0, targetPath.length - 1);
            }
        }
        try {
            newTarget = avm1ResolveTarget(ectx, targetPath, false);
            if (!avm1IsTarget(newTarget)) {
                avm1Warn('Invalid AVM1 target object: ' + targetPath);
                newTarget = undefined;
            }
        }
        catch (e) {
            avm1Warn('Unable to set target: ' + e);
        }
    }
    if (newTarget) {
        ectx.scopeList.flags |= AVM1ScopeListItemFlags.REPLACE_TARGET;
        ectx.scopeList.replaceTargetBy = newTarget;
    }
    else {
        ectx.scopeList.flags &= ~AVM1ScopeListItemFlags.REPLACE_TARGET;
        ectx.scopeList.replaceTargetBy = null;
    }
}
function avm1DefineFunction(ectx, actionsData, functionName, parametersNames, registersCount, registersAllocation, suppressArguments) {
    return new AVM1InterpretedFunction(ectx.context, ectx, actionsData, functionName, parametersNames, registersCount, registersAllocation, suppressArguments);
}
function avm1VariableNameHasPath(variableName) {
    return variableName && (variableName.indexOf('.') >= 0
        || variableName.indexOf(':') >= 0
        || variableName.indexOf('/') >= 0);
}
var cachedResolvedVariableResult = {
    scope: null,
    propertyName: null,
    value: undefined
};
function avm1IsTarget(target) {
    // TODO refactor
    return target instanceof AVM1Object && hasAwayJSAdaptee(target);
}
/* eslint-disable-next-line */
function avm1ResolveSimpleVariable(scopeList, variableName, flags, additionalName) {
    if (additionalName === void 0) { additionalName = null; }
    release || Debug.assert(alIsName(scopeList.scope.context, variableName));
    var currentTarget;
    var resolved = cachedResolvedVariableResult;
    for (var p = scopeList; p; p = p.previousScopeItem) {
        if ((p.flags & AVM1ScopeListItemFlags.REPLACE_TARGET) &&
            !(flags & 64 /* DISALLOW_TARGET_OVERRIDE */) &&
            !currentTarget) {
            currentTarget = p.replaceTargetBy;
        }
        if ((p.flags & AVM1ScopeListItemFlags.TARGET)) {
            if ((flags & 2 /* WRITE */)) {
                // last scope/target we can modify (exclude globals)
                resolved.scope = currentTarget || p.scope;
                resolved.propertyName = variableName;
                resolved.value = (flags & 32 /* GET_VALUE */)
                    ? resolved.scope.alGet(variableName)
                    : undefined;
                return resolved;
            }
            if ((flags & 1 /* READ */) && currentTarget) {
                if (currentTarget.alHasProperty(variableName)) {
                    resolved.scope = currentTarget;
                    resolved.propertyName = variableName;
                    resolved.value = (flags & 32 /* GET_VALUE */)
                        ? currentTarget.alGet(variableName)
                        : undefined;
                    return resolved;
                }
                continue;
            }
        }
        //console.log("scope :", p.scope.aCount);
        if (p.scope.alHasProperty(variableName)) {
            var value = p.scope.alGet(variableName);
            if (additionalName && (!value
                || typeof value !== 'object'
                || !value.alHasProperty
                || !value.alHasProperty(additionalName))) {
                continue;
            }
            resolved.scope = p.scope;
            resolved.propertyName = variableName;
            resolved.value = (flags & 32 /* GET_VALUE */) ? p.scope.alGet(variableName) : undefined;
            return resolved;
        }
        //80pro: in some cases we are trying to find a mc by name, but it is only registered as "this" within the scope
        // in this cases, we check if the "this" object actually has the name that we are searching for
        /*	if(p.scope.alHasProperty("this")) {
                var thisValue =	 (flags & AVM1ResolveVariableFlags.GET_VALUE) ? p.scope.alGet("this") : undefined;
                if(thisValue && thisValue.adaptee && thisValue.adaptee.name && thisValue.adaptee.name==variableName){
                    resolved.scope = p.scope;
                    resolved.propertyName = variableName;
                    resolved.value = thisValue;
                    return resolved;
                }
            }*/
    }
    noVarGetDebug || console.log('avm1ResolveSimpleVariable variableName', variableName);
    release || Debug.assert(!(flags & 2 /* WRITE */));
    return undefined;
}
/* eslint-disable-next-line */
function avm1ResolveVariable(ectx, variableName, flags) {
    // For now it is just very much magical -- designed to pass some of the swfdec tests
    // FIXME refactor
    release || Debug.assert(variableName);
    var len = variableName.length;
    var i = 0;
    var markedAsTarget = true;
    var resolved, ch, needsScopeResolution;
    var propertyName = null;
    var scope = null;
    var obj = undefined;
    // Canonicalizing the name here is ok even for paths: the only thing that (potentially)
    // happens is that the name is converted to lower-case, which is always valid for paths.
    // The original name is saved because the final property name needs to be extracted from
    // it for property name paths.
    var originalName = variableName;
    if (!avm1VariableNameHasPath(variableName)) {
        variableName = ectx.context.normalizeName(variableName);
        if (typeof variableName === 'string' && variableName.startsWith('_level')) {
            resolved = cachedResolvedVariableResult;
            resolved.scope = scope;
            resolved.propertyName = variableName;
            resolved.value = ectx.context.resolveLevel(+variableName[6]);
            return resolved;
        }
        //noVarGetDebug || console.log("simple variableName", variableName);
        var resolvedVar = avm1ResolveSimpleVariable(ectx.scopeList, variableName, flags);
        noVarGetDebug || console.log('resolved', resolvedVar);
        return resolvedVar;
    }
    noVarGetDebug || console.log('originalName', originalName);
    // if this is a path, and the last item is a "." flash will not find anything
    if (variableName[variableName.length - 1] == '.') {
        return null;
    }
    if (variableName[0] === '/') {
        noVarGetDebug || console.log('originalName starts with a \'/\'');
        resolved = avm1ResolveSimpleVariable(ectx.scopeList, '_root', 1 /* READ */ | 32 /* GET_VALUE */);
        if (resolved) {
            noVarGetDebug || console.log('resolved', resolved);
            propertyName = resolved.propertyName;
            scope = resolved.scope;
            obj = resolved.value;
        }
        i++;
        needsScopeResolution = false;
    }
    else {
        resolved = null;
        needsScopeResolution = true;
    }
    noVarGetDebug || console.log('needsScopeResolution', needsScopeResolution);
    if (i >= len) {
        return resolved;
    }
    var q = i;
    while (i < len) {
        if (!needsScopeResolution && !(obj instanceof AVM1Object)) {
            /* eslint-disable-next-line */
            noVarGetDebug || console.log('Unable to resolve variable on invalid object ' + variableName.substring(q, i - 1) + ' (expr ' + variableName + ')');
            /* eslint-disable-next-line */
            avm1Warn('Unable to resolve variable on invalid object ' + variableName.substring(q, i - 1) + ' (expr ' + variableName + ')');
            return null;
        }
        q = i;
        if (variableName[i] === '.' && variableName[i + 1] === '.') {
            i += 2;
            propertyName = '_parent';
        }
        else {
            while (i < len && ((ch = variableName[i]) !== '/' && ch !== '.' && ch !== ':')) {
                i++;
            }
            propertyName = variableName.substring(q, i);
        }
        if (propertyName === '' && i < len) {
            // Ignoring double delimiters in the middle of the path
            i++;
            continue;
        }
        scope = obj;
        var valueFound = false;
        if (markedAsTarget) {
            // Trying movie clip children first
            var child = obj instanceof AVM1MovieClip ? obj._lookupChildByName(propertyName) : void 0;
            if (child) {
                valueFound = true;
                obj = child;
            }
        }
        if (!valueFound) {
            if (needsScopeResolution) {
                // 80pro:
                // if we need to resolve the scope, we want to know the next property name
                // if a next property name exists, we pass it as extra argument to avm1ResolveSimpleVariable
                // this will make sure that avm1ResolveSimpleVariable
                // returns the scope that has the property name available
                q = i + 1;
                var k = i + 1;
                var nextPropName = '';
                if (variableName[k] === '.' && variableName[k + 1] === '.') {
                    k += 2;
                    nextPropName = '_parent';
                }
                else {
                    while (k < len && ((ch = variableName[k]) !== '/' && ch !== '.' && ch !== ':')) {
                        k++;
                    }
                    nextPropName = variableName.substring(q, k);
                }
                if (nextPropName == '')
                    nextPropName = null;
                resolved = avm1ResolveSimpleVariable(ectx.scopeList, propertyName, flags, nextPropName);
                if (!resolved && nextPropName) {
                    // if we tried to get with a nextPropName,
                    // and got nothing returned, we try again without any nextpropName
                    resolved = avm1ResolveSimpleVariable(ectx.scopeList, propertyName, flags);
                }
                if (resolved) {
                    valueFound = true;
                    propertyName = resolved.propertyName;
                    scope = resolved.scope;
                    obj = resolved.value;
                    if (i < len && !obj && scope) {
                        obj = scope;
                    }
                }
                needsScopeResolution = false;
            }
            else if (obj.alHasProperty(propertyName)) {
                obj = obj.alGet(propertyName);
                valueFound = true;
            }
        }
        if (!valueFound && propertyName[0] === '_') {
            // FIXME hacking to pass some swfdec test cases
            if (propertyName.startsWith('_level')) {
                obj = ectx.context.resolveLevel(+propertyName[6]);
                valueFound = true;
            }
            else if (propertyName === '_root') {
                obj = avm1ResolveRoot(ectx);
                valueFound = true;
            }
        }
        if (!valueFound && !(flags & 2 /* WRITE */)) {
            /* eslint-disable-next-line */
            avm1Warn('Unable to resolve ' + propertyName + ' on ' + variableName.substring(q, i - 1) + ' (expr ' + variableName + ')');
            return null;
        }
        if (i >= len) {
            break;
        }
        var delimiter = variableName[i++];
        if (delimiter === '/' && ((ch = variableName[i]) === ':' || ch === '.')) {
            delimiter = variableName[i++];
        }
        markedAsTarget = delimiter === '/';
    }
    resolved = cachedResolvedVariableResult;
    resolved.scope = scope;
    resolved.propertyName = originalName.substring(q, i);
    resolved.value = (flags & 32 /* GET_VALUE */) ? obj : undefined;
    return resolved;
}
function avm1GetTarget(ectx, allowOverride) {
    var scopeList = ectx.scopeList;
    for (var p = scopeList; p.previousScopeItem; p = p.previousScopeItem) {
        if ((p.flags & AVM1ScopeListItemFlags.REPLACE_TARGET) && allowOverride) {
            return p.replaceTargetBy;
        }
        if ((p.flags & AVM1ScopeListItemFlags.TARGET)) {
            return p.scope;
        }
    }
    release || Debug.assert(false, 'Shall not reach this statement');
    return undefined;
}
function avm1ResolveTarget(ectx, target, fromCurrentTarget) {
    var result;
    if (avm1IsTarget(target)) {
        result = target;
    }
    else {
        target = isNullOrUndefined(target) ? '' : alToString(this, target);
        if (target) {
            var targetPath = alToString(ectx.context, target);
            var resolved = avm1ResolveVariable(ectx, targetPath, 1 /* READ */ |
                128 /* ONLY_TARGETS */ |
                32 /* GET_VALUE */ |
                (fromCurrentTarget ? 0 : 64 /* DISALLOW_TARGET_OVERRIDE */));
            if (!resolved || !avm1IsTarget(resolved.value)) {
                avm1Warn('Invalid AVM1 target object: ' + targetPath);
                result = undefined;
            }
            else {
                result = resolved.value;
            }
        }
        else {
            result = avm1GetTarget(ectx, true);
        }
    }
    return result;
}
function avm1ResolveRoot(ectx) {
    var target = avm1GetTarget(ectx, true);
    return target.get_root();
}
function avm1ProcessWith(ectx, obj, withBlock) {
    if (isNullOrUndefined(obj)) {
        // Not executing anything in the block.
        avm1Warn('The with statement object cannot be undefined.');
        return;
    }
    var context = ectx.context;
    var scopeList = ectx.scopeList;
    var newScopeList = new AVM1ScopeListItem(alToObject(context, obj), scopeList);
    var newEctx = ectx.pushScope(newScopeList);
    interpretActionsData(newEctx, withBlock);
}
function avm1ProcessTry(ectx, catchIsRegisterFlag, finallyBlockFlag, catchBlockFlag, catchTarget, tryBlock, catchBlock, finallyBlock) {
    var currentContext = ectx.context;
    var scopeList = ectx.scopeList;
    var registers = ectx.registers;
    var savedTryCatchState = currentContext.isTryCatchListening;
    var caughtError;
    try {
        currentContext.isTryCatchListening = true;
        interpretActionsData(ectx.pushScope(), tryBlock);
    }
    catch (e) {
        currentContext.isTryCatchListening = savedTryCatchState;
        if (!catchBlockFlag || !(e instanceof AVM1Error)) {
            caughtError = e;
        }
        else {
            if (typeof catchTarget === 'string') { // TODO catchIsRegisterFlag?
                var scope = scopeList.scope;
                scope.alPut(catchTarget, e.error);
            }
            else {
                registers[catchTarget] = e.error;
            }
            interpretActionsData(ectx.pushScope(), catchBlock);
        }
    }
    currentContext.isTryCatchListening = savedTryCatchState;
    if (finallyBlockFlag) {
        interpretActionsData(ectx.pushScope(), finallyBlock);
    }
    if (caughtError) {
        throw caughtError;
    }
}
// SWF 3 actions
function avm1_0x81_ActionGotoFrame(ectx, args) {
    var frame = args[0];
    var play = args[1];
    if (play) {
        ectx.actions.gotoAndPlay(frame + 1);
    }
    else {
        ectx.actions.gotoAndStop(frame + 1);
    }
}
function avm1_0x83_ActionGetURL(ectx, args) {
    // const actions = ectx.actions;
    var urlString = args[0];
    var targetString = args[1];
    ectx.actions.getURL(urlString, targetString);
}
function avm1_0x04_ActionNextFrame(ectx) {
    ectx.actions.nextFrame();
}
function avm1_0x05_ActionPreviousFrame(ectx) {
    ectx.actions.prevFrame();
}
function avm1_0x06_ActionPlay(ectx) {
    ectx.actions.play();
}
function avm1_0x07_ActionStop(ectx) {
    ectx.actions.stop();
}
function avm1_0x08_ActionToggleQuality(ectx) {
    ectx.actions.toggleHighQuality();
}
function avm1_0x09_ActionStopSounds(ectx) {
    ectx.actions.stopAllSounds();
}
function avm1_0x8A_ActionWaitForFrame(ectx, args) {
    var frame = args[0];
    // const count: number = args[1];
    return !ectx.actions.ifFrameLoaded(frame);
}
function avm1_0x8B_ActionSetTarget(ectx, args) {
    var targetName = args[0];
    avm1SetTarget(ectx, targetName);
}
function avm1_0x8C_ActionGoToLabel(ectx, args) {
    var label = args[0];
    var play = args[1];
    if (play) {
        ectx.actions.gotoAndPlay(label);
    }
    else {
        ectx.actions.gotoAndStop(label);
    }
}
// SWF 4 actions
function avm1_0x96_ActionPush(ectx, args) {
    var registers = ectx.registers;
    var constantPool = ectx.constantPool;
    var stack = ectx.stack;
    args.forEach(function (value) {
        if (value instanceof ParsedPushConstantAction) {
            stack.push(constantPool[value.constantIndex]);
        }
        else if (value instanceof ParsedPushRegisterAction) {
            var registerNumber = value.registerNumber;
            if (registerNumber < 0 || registerNumber >= registers.length) {
                stack.push(undefined);
            }
            else {
                stack.push(registers[registerNumber]);
            }
        }
        else {
            stack.push(value);
        }
    });
}
function avm1_0x17_ActionPop(ectx) {
    var stack = ectx.stack;
    stack.pop();
}
function avm1_0x0A_ActionAdd(ectx) {
    var stack = ectx.stack;
    var a = alToNumber(ectx.context, stack.pop());
    var b = alToNumber(ectx.context, stack.pop());
    if (!ectx.isSwfVersion7) {
        if (typeof a === 'undefined')
            a = 0;
        if (typeof b === 'undefined')
            b = 0;
    }
    if (!isFinite(a) || !isFinite(b)) {
        if (a === -Infinity && b === -Infinity)
            stack.push(-Infinity);
        else if (isNaN(a) || isNaN(b))
            stack.push(NaN);
        else if (a === b)
            stack.push(Infinity);
        else if (!isFinite(a) && !isFinite(b))
            stack.push(NaN);
        else if (!isFinite(a))
            stack.push(a);
        else if (!isFinite(b))
            stack.push(b);
    }
    else if (b == null) {
        stack.push(NaN);
    }
    else {
        stack.push(a + b);
    }
}
function avm1_0x0B_ActionSubtract(ectx) {
    var stack = ectx.stack;
    var a = stack.pop();
    var b = stack.pop();
    a = alToNumber(ectx.context, a);
    b = alToNumber(ectx.context, b);
    if (!ectx.isSwfVersion7) {
        if (a === null || typeof a === 'undefined')
            a = 0;
        if (b === null || typeof b === 'undefined')
            b = 0;
    }
    if (!isFinite(a) || !isFinite(b)) {
        if (isNaN(a) || isNaN(b))
            stack.push(NaN);
        else if (a === b)
            stack.push(NaN);
        else if (!isFinite(a)) {
            if (a === -Infinity)
                stack.push(Infinity);
            else
                stack.push(-Infinity);
        }
        else if (!isFinite(b))
            stack.push(b);
    }
    else {
        stack.push(b - a);
    }
}
function avm1_0x0C_ActionMultiply(ectx) {
    var stack = ectx.stack;
    var a = stack.pop();
    if (a === '\n')
        a = NaN;
    else
        a = alToNumber(ectx.context, a);
    var b = stack.pop();
    if (b === '\n')
        b = NaN;
    else
        b = alToNumber(ectx.context, b);
    if (!ectx.isSwfVersion7) {
        if (a == null || typeof a === 'undefined')
            a = 0;
        if (b == null || typeof b === 'undefined')
            b = 0;
    }
    if (!isFinite(a) || !isFinite(b)) {
        if (isNaN(a) || isNaN(b))
            stack.push(NaN);
        else if (a === b)
            stack.push(Infinity);
        else if (!isFinite(a) && !isFinite(b))
            stack.push(-Infinity);
        else if (!isFinite(a)) {
            if (b == 0)
                stack.push(NaN);
            else if (a >= 0) {
                if (b >= 0)
                    stack.push(Infinity);
                else
                    stack.push(-Infinity);
            }
            else {
                if (b >= 0)
                    stack.push(-Infinity);
                else
                    stack.push(Infinity);
            }
        }
        else if (!isFinite(b)) {
            if (a == 0)
                stack.push(NaN);
            else if (b >= 0) {
                if (a >= 0)
                    stack.push(Infinity);
                else
                    stack.push(-Infinity);
            }
            else {
                if (a >= 0)
                    stack.push(-Infinity);
                else
                    stack.push(Infinity);
            }
        }
    }
    else {
        stack.push(a * b);
    }
}
function avm1_0x0D_ActionDivide(ectx) {
    var stack = ectx.stack;
    var a = stack.pop();
    var b = stack.pop();
    var type_a = typeof a;
    var type_b = typeof b;
    if (!ectx.isSwfVersion7) {
        // for SWF version < 7:
        // undefined and null get converted to 0
        if (a === null || type_a === 'undefined') {
            a = 0;
            type_a = 'number';
        }
        if (b === null || type_b === 'undefined') {
            b = 0;
            type_b = 'number';
        }
    }
    if (type_a === 'object' || type_b === 'object'
        || type_a === 'string' || type_b === 'string'
        || type_a === 'undefined' || type_b === 'undefined'
        || isNaN(a) || isNaN(b)) {
        stack.push(NaN);
        return;
    }
    if (type_a === 'boolean') {
        a = +a;
    }
    else
        a = alToNumber(ectx.context, a);
    if (type_b === 'boolean') {
        b = +b;
    }
    else
        b = alToNumber(ectx.context, b);
    if (!isFinite(a) || !isFinite(b) || (a == 0 && b == 0)) {
        if ((a == 0 && b == 0) || (!isFinite(a) && !isFinite(b)))
            stack.push(NaN);
        else if (a == 0)
            stack.push(b);
        else if (b == 0)
            stack.push(0);
        else if (!isFinite(b)) {
            if (b >= 0) {
                if (a >= 0)
                    stack.push(Infinity);
                else
                    stack.push(-Infinity);
            }
            else {
                if (a >= 0)
                    stack.push(-Infinity);
                else
                    stack.push(Infinity);
            }
        }
        else if (!isFinite(a)) {
            if (isNaN(a))
                stack.push(a);
            else
                stack.push(0);
        }
    }
    else if (a == 0) {
        stack.push((b > 0) ? Infinity : -Infinity);
    }
    else {
        stack.push(b / a);
    }
}
function avm1_0x0E_ActionEquals(ectx) {
    var stack = ectx.stack;
    var isSwfVersion5 = ectx.isSwfVersion5;
    var a = alToNumber(ectx.context, stack.pop());
    var b = alToNumber(ectx.context, stack.pop());
    // Calculate the difference.
    var ma = Math.abs(a), mb = Math.abs(b);
    var larges = ma > mb ? ma : mb;
    var eps = (1e-6) * larges;
    var f = Math.abs(a - b) <= eps;
    stack.push(isSwfVersion5 ? f : f ? 1 : 0);
}
function avm1_0x0F_ActionLess(ectx) {
    var stack = ectx.stack;
    var isSwfVersion5 = ectx.isSwfVersion5;
    var a = alToNumber(ectx.context, stack.pop());
    var b = alToNumber(ectx.context, stack.pop());
    var f = b < a;
    stack.push(isSwfVersion5 ? f : f ? 1 : 0);
}
function avm1_0x10_ActionAnd(ectx) {
    var stack = ectx.stack;
    var isSwfVersion5 = ectx.isSwfVersion5;
    var a = alToBoolean(ectx.context, stack.pop());
    var b = alToBoolean(ectx.context, stack.pop());
    var f = a && b;
    stack.push(isSwfVersion5 ? f : f ? 1 : 0);
}
function avm1_0x11_ActionOr(ectx) {
    var stack = ectx.stack;
    var isSwfVersion5 = ectx.isSwfVersion5;
    var a = alToBoolean(ectx.context, stack.pop());
    var b = alToBoolean(ectx.context, stack.pop());
    var f = a || b;
    stack.push(isSwfVersion5 ? f : f ? 1 : 0);
}
function avm1_0x12_ActionNot(ectx) {
    var avm = ectx.context;
    var stack = ectx.stack;
    var v = stack.pop();
    /*
    if(ectx.context.swfVersion <= 6 && typeof v === 'string'){
        v = false;
    }*/
    var f = !alToBoolean(avm, v);
    stack.push(bToRuntimeBool(avm, f));
}
function avm1_0x13_ActionStringEquals(ectx) {
    var stack = ectx.stack;
    var isSwfVersion5 = ectx.isSwfVersion5;
    var sa = alToString(ectx.context, stack.pop());
    var sb = alToString(ectx.context, stack.pop());
    var f = sa == sb;
    stack.push(isSwfVersion5 ? f : f ? 1 : 0);
}
function avm1_0x14_ActionStringLength(ectx) {
    var stack = ectx.stack;
    var sa = alToString(ectx.context, stack.pop());
    stack.push(ectx.actions.length_(sa));
}
function avm1_0x31_ActionMBStringLength(ectx) {
    var stack = ectx.stack;
    var sa = alToString(ectx.context, stack.pop());
    stack.push(ectx.actions.length_(sa));
}
function avm1_0x21_ActionStringAdd(ectx) {
    var stack = ectx.stack;
    var sa = alToString(ectx.context, stack.pop());
    var sb = alToString(ectx.context, stack.pop());
    stack.push(sb + sa);
}
function avm1_0x15_ActionStringExtract(ectx) {
    var stack = ectx.stack;
    var count = stack.pop();
    var index = stack.pop();
    var value = alToString(ectx.context, stack.pop());
    stack.push(ectx.actions.substring(value, index, count));
}
function avm1_0x35_ActionMBStringExtract(ectx) {
    var stack = ectx.stack;
    var count = stack.pop();
    var index = stack.pop();
    var value = alToString(ectx.context, stack.pop());
    stack.push(ectx.actions.mbsubstring(value, index, count));
}
function avm1_0x29_ActionStringLess(ectx) {
    var stack = ectx.stack;
    var isSwfVersion5 = ectx.isSwfVersion5;
    var sa = alToString(ectx.context, stack.pop());
    var sb = alToString(ectx.context, stack.pop());
    var f = sb < sa;
    stack.push(isSwfVersion5 ? f : f ? 1 : 0);
}
function avm1_0x18_ActionToInteger(ectx) {
    var stack = ectx.stack;
    var value = alToInt32(ectx.context, stack.pop());
    stack.push(value);
}
function avm1_0x32_ActionCharToAscii(ectx) {
    var stack = ectx.stack;
    var ch = stack.pop();
    var charCode = ectx.actions.ord(ch);
    stack.push(charCode);
}
function avm1_0x36_ActionMBCharToAscii(ectx) {
    var stack = ectx.stack;
    var ch = stack.pop();
    var charCode = ectx.actions.mbord(ch);
    stack.push(charCode);
}
function avm1_0x33_ActionAsciiToChar(ectx) {
    var stack = ectx.stack;
    var charCode = +stack.pop();
    var ch = ectx.actions.chr(charCode);
    stack.push(ch);
}
function avm1_0x37_ActionMBAsciiToChar(ectx) {
    var stack = ectx.stack;
    var charCode = +stack.pop();
    var ch = ectx.actions.mbchr(charCode);
    stack.push(ch);
}
function avm1_0x99_ActionJump(ectx, args) {
    // implemented in the analyzer
}
function avm1_0x9D_ActionIf(ectx, args) {
    var stack = ectx.stack;
    // const offset: number = args[0];
    return !!stack.pop();
}
function avm1_0x9E_ActionCall(ectx) {
    var stack = ectx.stack;
    var label = stack.pop();
    ectx.actions.call(label);
}
function avm1_0x1C_ActionGetVariable(ectx) {
    var stack = ectx.stack;
    var variableName = stack.pop();
    if (variableName)
        variableName = alToString(ectx.context, variableName);
    var sp = stack.length;
    stack.push(undefined);
    var method = avm1ResolveVariable(ectx, '__get__' + variableName, 1 /* READ */ | 32 /* GET_VALUE */);
    // call __set__
    if (method && method.value) {
        var _a = avm1_callableHelper(ectx, method.scope, method.propertyName, []), result = _a.result, called = _a.called;
        if (called) {
            stack[sp] = result;
            return;
        }
    }
    var resolved = avm1ResolveVariable(ectx, variableName, 1 /* READ */ | 32 /* GET_VALUE */);
    if (isNullOrUndefined(resolved)) {
        if (variableName == 'TRUE') {
            stack[sp] = true;
        }
        else if (variableName == 'FALSE') {
            stack[sp] = false;
        }
        else if (variableName && variableName.indexOf('this.') == 0) {
            variableName = variableName.replace('this.', '');
            resolved = avm1ResolveVariable(ectx, variableName, 1 /* READ */ | 32 /* GET_VALUE */);
            stack[sp] = resolved ? resolved.value : undefined;
        }
        else if (avm1WarningsEnabled.value) {
            avm1Warn('AVM1 warning: cannot look up variable \'' + variableName + '\'');
        }
        return;
    }
    stack[sp] = resolved ? resolved.value : undefined;
}
function avm1_0x1D_ActionSetVariable(ectx) {
    var stack = ectx.stack;
    var value = stack.pop();
    var variableName = '' + stack.pop();
    var method = avm1ResolveVariable(ectx, '__set__' + variableName, 1 /* READ */ | 32 /* GET_VALUE */);
    // call __set__
    if (method && method.value) {
        var state = avm1_callableHelper(ectx, method.scope, method.propertyName, [value]);
        if (state.called) {
            return;
        }
    }
    var resolved = avm1ResolveVariable(ectx, variableName, 2 /* WRITE */);
    // try to call __get__name
    if (!resolved) {
        if (avm1WarningsEnabled.value) {
            avm1Warn('AVM1 warning: cannot look up variable \'' + variableName + '\'');
        }
        //console.log("avm1_0x1D_ActionSetVariable", resolved, variableName, value);
        return;
    }
    release || assert(resolved.propertyName);
    resolved.scope.alPut(resolved.propertyName, value);
    as2SyncEvents(ectx.context, resolved.propertyName, resolved.scope);
    //console.log("avm1_0x1D_ActionSetVariable", resolved, variableName, value);
}
function avm1_0x9A_ActionGetURL2(ectx, args) {
    var stack = ectx.stack;
    var flags = args[0];
    var target = stack.pop();
    var url = stack.pop();
    var sendVarsMethod;
    if (flags & 1) {
        sendVarsMethod = 'GET';
    }
    else if (flags & 2) {
        sendVarsMethod = 'POST';
    }
    var loadTargetFlag = flags & 1 << 6;
    var loadVariablesFlag = flags & 1 << 7;
    if (loadVariablesFlag) {
        ectx.actions.loadVariables(url, target, sendVarsMethod);
    }
    else if (!loadTargetFlag) {
        ectx.actions.getURL(url, target, sendVarsMethod);
    }
    else {
        ectx.actions.loadMovie(url, target, sendVarsMethod);
    }
}
function avm1_0x9F_ActionGotoFrame2(ectx, args) {
    var stack = ectx.stack;
    var flags = args[0];
    var gotoParams = [stack.pop()];
    if (flags & 2) {
        gotoParams.push(args[1]);
    }
    var gotoMethod = flags & 1 ? ectx.actions.gotoAndPlay : ectx.actions.gotoAndStop;
    gotoMethod.apply(ectx.actions, gotoParams);
}
function avm1_0x20_ActionSetTarget2(ectx) {
    var stack = ectx.stack;
    var target = alToString(ectx.context, stack.pop());
    avm1SetTarget(ectx, target);
}
function avm1_0x22_ActionGetProperty(ectx) {
    var stack = ectx.stack;
    var index = stack.pop();
    var target = stack.pop();
    var sp = stack.length;
    stack.push(undefined);
    var resolved = avm1ResolveTarget(ectx, target, true);
    var propertyName = MovieClipProperties[index];
    if (resolved && propertyName) {
        stack[sp] = resolved.alGet(propertyName);
    }
    //console.log("avm1_0x22_ActionGetProperty", resolved, propertyName, stack[sp]);
}
function avm1_0x23_ActionSetProperty(ectx) {
    var stack = ectx.stack;
    var value = stack.pop();
    var index = stack.pop();
    var target = stack.pop();
    var resolved = avm1ResolveTarget(ectx, target, true);
    var propertyName = MovieClipProperties[index];
    if (resolved && propertyName) {
        resolved.alPut(propertyName, value);
    }
    //console.log("avm1_0x23_ActionSetProperty", resolved, propertyName, value);
}
function avm1_0x24_ActionCloneSprite(ectx) {
    var stack = ectx.stack;
    var depth = stack.pop();
    var target = stack.pop();
    var source = stack.pop();
    ectx.actions.duplicateMovieClip(source, target, depth);
}
function avm1_0x25_ActionRemoveSprite(ectx) {
    var stack = ectx.stack;
    var target = stack.pop();
    ectx.actions.removeMovieClip(target);
}
function avm1_0x27_ActionStartDrag(ectx) {
    var stack = ectx.stack;
    var method = ectx.actions.startDrag;
    var target = stack.pop();
    var lockcenter = stack.pop();
    var constrain = !!stack.pop();
    if (constrain) {
        var y2 = stack.pop();
        var x2 = stack.pop();
        var y1 = stack.pop();
        var x1 = stack.pop();
        method.call(ectx.actions, target, lockcenter, x1, y1, x2, y2);
    }
    else {
        method.call(ectx.actions, target, lockcenter);
    }
}
function avm1_0x28_ActionEndDrag(ectx) {
    ectx.actions.stopDrag();
}
function avm1_0x8D_ActionWaitForFrame2(ectx, args) {
    var stack = ectx.stack;
    //const count: number = args[0];
    var frame = stack.pop();
    return !ectx.actions.ifFrameLoaded(frame);
}
function avm1_0x26_ActionTrace(ectx) {
    var stack = ectx.stack;
    var value = stack.pop();
    ectx.actions.trace(value);
}
function avm1_0x34_ActionGetTime(ectx) {
    var stack = ectx.stack;
    stack.push(ectx.actions.getTimer());
}
function avm1_0x30_ActionRandomNumber(ectx) {
    var stack = ectx.stack;
    stack.push(ectx.actions.random(stack.pop()));
}
// SWF 5
function avm1_0x3D_ActionCallFunction(ectx) {
    var stack = ectx.stack;
    var functionName = stack.pop();
    if (alIsArray(ectx.context, functionName))
        functionName = functionName.value.join('.');
    var args = avm1ReadFunctionArgs(stack);
    var sp = stack.length;
    stack.push(undefined);
    var resolved = avm1ResolveVariable(ectx, functionName, 1 /* READ */ | 32 /* GET_VALUE */);
    if (isNullOrUndefined(resolved)) {
        avm1Warn('AVM1 warning: cannot look up function \'' + functionName + '\'');
        return;
    }
    var fn = resolved.value;
    // AVM1 simply ignores attempts to invoke non-functions.
    if (!alIsFunction(fn)) {
        avm1Warn('AVM1 warning: function \'' + functionName +
            (fn ? '\' is not callable' : '\' is undefined'));
        return;
    }
    release || assert(stack.length === sp + 1);
    if (functionName.toLowerCase() == 'array')
        args = [args];
    // REDUX
    stack[sp] = fn.alCall(resolved.scope || null, args);
}
function avm1_0x52_ActionCallMethod(ectx) {
    var stack = ectx.stack;
    var methodName = stack.pop();
    var obj = stack.pop();
    var args = avm1ReadFunctionArgs(stack);
    var sp = stack.length;
    stack.push(undefined);
    var call = avm1_callableHelper(ectx, obj, methodName, args);
    if (call.called)
        stack[sp] = call.result;
}
/* eslint-disable-next-line max-len */
function avm1_callableHelper(ectx, obj, methodName, args) {
    // AVM1 simply ignores attempts to invoke methods on non-existing objects.
    if (isNullOrUndefined(obj)) {
        avm1Warn('AVM1 warning: method \'' + methodName + '\' can\'t be called on undefined object');
        return { called: false, result: undefined };
    }
    var frame = ectx.context.frame;
    var superArg;
    var fn;
    var target;
    var result;
    var called = false;
    // Per spec, a missing or blank method name causes the container to be treated as
    // a function to call.
    if (isNullOrUndefined(methodName) || methodName === '') {
        if (obj instanceof AVM1SuperWrapper) {
            var superFrame = obj.callFrame;
            superArg = avm1FindSuperPropertyOwner(ectx.context, superFrame, '__constructor__');
            if (superArg) {
                fn = superArg.alGet('__constructor__');
                target = superFrame.currentThis;
            }
        }
        else {
            // For non-super calls, we call obj with itself as the target.
            // TODO: ensure this is correct.
            fn = obj;
            target = obj;
        }
        // AVM1 simply ignores attempts to invoke non-functions.
        if (alIsFunction(fn)) {
            frame.setCallee(target, superArg, fn, args);
            result = fn.alCall(target, args);
            called = true;
            frame.resetCallee();
        }
        else {
            avm1Warn('AVM1 warning: obj \'' + obj + (obj ? '\' is not callable' : '\' is undefined'));
        }
        //release || assert(stack.length === sp + 1);
        return { result: result, called: called };
    }
    if (obj instanceof AVM1SuperWrapper) {
        var superFrame = obj.callFrame;
        superArg = avm1FindSuperPropertyOwner(ectx.context, superFrame, methodName);
        if (superArg) {
            fn = superArg.alGet(methodName);
            target = superFrame.currentThis;
        }
    }
    else {
        fn = as2GetProperty(ectx.context, obj, methodName);
        target = alToObject(ectx.context, obj);
    }
    // AVM1 simply ignores attempts to invoke non-methods.
    if (!alIsFunction(fn)) {
        // we might have injected js function here, so we call that
        if (typeof fn === 'function') {
            // do something
            result = fn(args);
            called = true;
        }
        else {
            avm1Warn('AVM1 warning: method \'' + methodName + '\' on object', obj, (isNullOrUndefined(fn) ?
                'is undefined' :
                'is not callable'));
        }
        return { result: result, called: called };
    }
    //release || assert(stack.length === sp + 1);
    frame.setCallee(target, superArg, fn, args);
    if (methodName == 'toString' && typeof obj == 'number') {
        if (args.length == 1 && args[0] == 16) {
            result = obj.toString(16);
        }
        else {
            result = alToString(ectx.context, obj);
            called = true;
        }
    }
    else {
        result = fn.alCall(target, args);
        called = true;
    }
    frame.resetCallee();
    return { result: result, called: called };
}
function avm1_0x88_ActionConstantPool(ectx, args) {
    var constantPool = args[0];
    ectx.constantPool = constantPool;
}
function avm1_0x9B_ActionDefineFunction(ectx, args) {
    var stack = ectx.stack;
    var functionBody = args[0];
    var functionName = args[1];
    var functionParams = args[2];
    var fn = avm1DefineFunction(ectx, functionBody, functionName, functionParams, 4, null, 0);
    if (functionName) {
        var scope = ectx.scopeList.scope;
        scope.alPut(functionName, fn);
        as2SyncEvents(ectx.context, functionName, scope);
    }
    else {
        stack.push(fn);
    }
}
function avm1_0x3C_ActionDefineLocal(ectx) {
    var stack = ectx.stack;
    var scope = ectx.scopeList.scope;
    var value = stack.pop();
    var name = stack.pop();
    /*if(typeof value==="undefined" && scope.alHasProperty(name)){
        return;
    }*/
    scope.alPut(name, value);
}
function avm1_0x41_ActionDefineLocal2(ectx) {
    var stack = ectx.stack;
    var scope = ectx.scopeList.scope;
    var name = stack.pop();
    if (scope.alHasProperty(name)) {
        return;
    }
    scope.alPut(name, undefined);
}
function avm1_0x3A_ActionDelete(ectx) {
    var stack = ectx.stack;
    var name = stack.pop();
    var obj = stack.pop();
    if (isNullOrUndefined(obj)) {
        // AVM1 just ignores delete on non-existant containers.
        avm1Warn('AVM1 warning: cannot delete member \'' + name + '\' on undefined object');
        return;
    }
    stack.push(as2DeleteProperty(ectx.context, obj, name));
}
function avm1_0x3B_ActionDelete2(ectx) {
    var stack = ectx.stack;
    var name = stack.pop();
    var resolved = avm1ResolveVariable(ectx, name, 1 /* DELETE */);
    if (isNullOrUndefined(resolved)) {
        avm1Warn('AVM1 warning: cannot look up variable \'' + name + '\'');
        return;
    }
    var targetValue = resolved.scope.alGetProperty(name);
    if (targetValue && targetValue.value && targetValue.value.adaptee) {
        // this is a instance of a symbol. do not delete it
        return;
    }
    stack.push(as2DeleteProperty(ectx.context, resolved.scope, name));
}
function avm1_0x46_ActionEnumerate(ectx) {
    var stack = ectx.stack;
    var objectName = stack.pop();
    stack.push(null);
    var resolved = avm1ResolveVariable(ectx, objectName, 1 /* READ */ | 32 /* GET_VALUE */);
    if (isNullOrUndefined(resolved)) {
        avm1Warn('AVM1 warning: cannot look up variable \'' + objectName + '\'');
        return;
    }
    var obj = resolved.value;
    if (isNullOrUndefined(obj)) {
        avm1Warn('AVM1 warning: cannot iterate over undefined object');
        return;
    }
    as2Enumerate(obj, function (name) {
        stack.push(name);
    }, null);
}
function avm1_0x49_ActionEquals2(ectx) {
    var stack = ectx.stack;
    var a = stack.pop();
    var b = stack.pop();
    stack.push(as2Equals(ectx.context, a, b));
}
function avm1_0x4E_ActionGetMember(ectx, args) {
    var stack = ectx.stack;
    var name = args ? args[1] : stack.pop();
    var obj = args ? args[0] : stack.pop();
    var loops = !args ? 1 : args.length - 1;
    var result = void 0;
    // serach over all args, first is object, next - chained name field.
    var i = 0;
    while (i < loops) {
        if (obj == null) {
            // AVM1 just ignores gets on non-existant containers.
            avm1Warn('AVM1 warning: cannot get member \'' + name + '\' on undefined object');
            return stack[stack.length] = void 0;
        }
        if (obj instanceof AVM1SuperWrapper) {
            var superFrame = obj.callFrame;
            var superArg = avm1FindSuperPropertyOwner(ectx.context, superFrame, name);
            if (superArg) {
                result = superArg.alGet(name);
            }
        }
        else {
            result = as2GetProperty(ectx.context, obj, name);
        }
        if (i === loops - 1) {
            return stack[stack.length] = result;
        }
        obj = result;
        name = args[i + 2];
        i++;
    }
    return stack[stack.length] = result;
}
function avm1_0x42_ActionInitArray(ectx) {
    var stack = ectx.stack;
    var obj = new AVM1ArrayNative(ectx.context, avm1ReadFunctionArgs(stack));
    stack.push(obj);
}
function avm1_0x43_ActionInitObject(ectx) {
    var stack = ectx.stack;
    var count = +stack.pop();
    count = fixArgsCount(count, stack.length >> 1);
    var obj = alNewObject(ectx.context);
    for (var i = 0; i < count; i++) {
        var value = stack.pop();
        var name_1 = stack.pop();
        obj.alPut(name_1, value);
    }
    stack.push(obj);
}
function avm1_0x53_ActionNewMethod(ectx) {
    var stack = ectx.stack;
    var methodName = stack.pop();
    var obj = stack.pop();
    var args = avm1ReadFunctionArgs(stack);
    var sp = stack.length;
    stack.push(undefined);
    // AVM1 simply ignores attempts to construct methods on non-existing objects.
    if (isNullOrUndefined(obj)) {
        avm1Warn('AVM1 warning: method \'' + methodName + '\' can\'t be constructed on undefined object');
        return;
    }
    var ctor;
    // Per spec, a missing or blank method name causes the container to be treated as
    // a function to construct.
    if (isNullOrUndefined(methodName) || methodName === '') {
        ctor = obj;
    }
    else {
        ctor = as2GetProperty(ectx.context, obj, methodName);
    }
    var result = as2Construct(ctor, args);
    if (result === undefined) {
        avm1Warn('AVM1 warning: method \'' + methodName + '\' on object', obj, 'is not constructible');
    }
    stack[sp] = result;
    release || assert(stack.length === sp + 1);
}
function avm1_0x40_ActionNewObject(ectx) {
    var stack = ectx.stack;
    var objectName = stack.pop();
    var args = avm1ReadFunctionArgs(stack);
    var sp = stack.length;
    stack.push(undefined);
    var resolved = avm1ResolveVariable(ectx, objectName, 1 /* READ */ | 32 /* GET_VALUE */);
    if (isNullOrUndefined(resolved)) {
        avm1Warn('AVM1 warning: cannot look up object \'' + objectName + '\'');
        return;
    }
    var obj = resolved.value;
    var result = createBuiltinType(ectx.context, obj, args);
    if (result === undefined) {
        // obj in not a built-in type
        result = as2Construct(obj, args);
        if (result === undefined) {
            avm1Warn('AVM1 warning: object \'' + objectName +
                (obj ? '\' is not constructible' : '\' is undefined'));
        }
    }
    release || assert(stack.length === sp + 1);
    stack[sp] = result;
}
function avm1_0x4F_ActionSetMember(ectx, args) {
    var stack = ectx.stack;
    var value = args ? args[2] : stack.pop();
    var name = args ? args[1] : stack.pop();
    var obj = args ? args[0] : stack.pop();
    if (isNullOrUndefined(obj)) {
        // AVM1 just ignores sets on non-existant containers
        avm1Warn('AVM1 warning: cannot set member \'' + name + '\' on undefined object');
        return;
    }
    if (obj instanceof AVM1SuperWrapper) {
        avm1Warn('AVM1 warning: cannot set member \'' + name + '\' on super');
        return;
    }
    if (typeof name === 'undefined')
        return;
    as2SetProperty(ectx.context, obj, name, value);
}
function avm1_0x45_ActionTargetPath(ectx) {
    var stack = ectx.stack;
    var obj = stack.pop();
    stack.push(isAVM1MovieClip(obj) ? obj._target : void (0));
}
function avm1_0x94_ActionWith(ectx, args) {
    var stack = ectx.stack;
    var withBody = args[0];
    var obj = stack.pop();
    avm1ProcessWith(ectx, obj, withBody);
}
function avm1_0x4A_ActionToNumber(ectx) {
    var stack = ectx.stack;
    stack.push(alToNumber(ectx.context, stack.pop()));
}
function avm1_0x4B_ActionToString(ectx) {
    var stack = ectx.stack;
    stack.push(alToString(ectx.context, stack.pop()));
}
function avm1_0x44_ActionTypeOf(ectx) {
    var stack = ectx.stack;
    var obj = stack.pop();
    var result = as2GetType(obj);
    stack.push(result);
}
function avm1_0x47_ActionAdd2(ectx, a, b) {
    var stack = ectx.stack;
    if (arguments.length > 1) {
        b = alToPrimitive(ectx.context, b);
        a = alToPrimitive(ectx.context, a);
    }
    else {
        b = alToPrimitive(ectx.context, stack.pop());
        a = alToPrimitive(ectx.context, stack.pop());
    }
    var ta = typeof a;
    var tb = typeof b;
    var aIsString = ta === 'string';
    var bIsString = tb === 'string';
    if (aIsString && bIsString) {
        return stack[stack.length] = a + b;
    }
    else if (aIsString || bIsString) {
        return stack[stack.length] =
            (aIsString ? a : alToString(ectx.context, a)) +
                (bIsString ? b : alToString(ectx.context, b));
    }
    else {
        if (!ectx.isSwfVersion7) {
            if (a === null || ta === 'undefined')
                a = 0;
            if (b === null || tb === 'undefined')
                b = 0;
        }
        if ((b == null) || (a == null)) {
            stack.push(NaN);
        }
        else if (!isFinite(a) || !isFinite(b)) {
            if (a === -Infinity && b === -Infinity)
                stack.push(-Infinity);
            else if (isNaN(a) || isNaN(b))
                stack.push(NaN);
            else if (a === b)
                stack.push(Infinity);
            else if (!isFinite(a) && !isFinite(b))
                stack.push(NaN);
            else if (!isFinite(a))
                stack.push(a);
            else if (!isFinite(b))
                stack.push(b);
        }
        else {
            stack.push(a + b);
        }
    }
    return stack[stack.length - 1];
}
function avm1_0x48_ActionLess2(ectx) {
    var stack = ectx.stack;
    var a = stack.pop();
    var b = stack.pop();
    stack.push(as2Compare(ectx.context, b, a));
}
function avm1_0x3F_ActionModulo(ectx) {
    var stack = ectx.stack;
    var a = stack.pop();
    var b = stack.pop();
    if (typeof a === 'string' || typeof b === 'string') {
        stack.push(NaN);
        return;
    }
    a = alToNumber(ectx.context, a);
    b = alToNumber(ectx.context, b);
    if (!ectx.isSwfVersion7) {
        if (typeof a === 'undefined')
            a = 0;
        if (typeof b === 'undefined')
            b = 0;
    }
    if (!isFinite(a) || !isFinite(b) || (a == 0 && b == 0)) {
        if (isNaN(a) || isNaN(b) || (!isFinite(a) && !isFinite(b)))
            stack.push(NaN);
        else if (!isFinite(a))
            stack.push(b);
        else
            stack.push(NaN);
    }
    else {
        stack.push(b % a);
    }
}
function avm1_0x60_ActionBitAnd(ectx) {
    var stack = ectx.stack;
    var a = alToInt32(ectx.context, stack.pop());
    var b = alToInt32(ectx.context, stack.pop());
    stack.push(b & a);
}
function avm1_0x63_ActionBitLShift(ectx) {
    var stack = ectx.stack;
    var a = alToInt32(ectx.context, stack.pop());
    var b = alToInt32(ectx.context, stack.pop());
    stack.push(b << a);
}
function avm1_0x61_ActionBitOr(ectx) {
    var stack = ectx.stack;
    var a = alToInt32(ectx.context, stack.pop());
    var b = alToInt32(ectx.context, stack.pop());
    stack.push(b | a);
}
function avm1_0x64_ActionBitRShift(ectx) {
    var stack = ectx.stack;
    var a = alToInt32(ectx.context, stack.pop());
    var b = alToInt32(ectx.context, stack.pop());
    stack.push(b >> a);
}
function avm1_0x65_ActionBitURShift(ectx) {
    var stack = ectx.stack;
    var a = alToInt32(ectx.context, stack.pop());
    var b = alToInt32(ectx.context, stack.pop());
    stack.push(b >>> a);
}
function avm1_0x62_ActionBitXor(ectx) {
    var stack = ectx.stack;
    var a = alToInt32(ectx.context, stack.pop());
    var b = alToInt32(ectx.context, stack.pop());
    stack.push(b ^ a);
}
function avm1_0x51_ActionDecrement(ectx) {
    var stack = ectx.stack;
    var a = alToNumber(ectx.context, stack.pop());
    a--;
    stack.push(a);
}
function avm1_0x50_ActionIncrement(ectx) {
    var stack = ectx.stack;
    var a = alToNumber(ectx.context, stack.pop());
    a++;
    stack.push(a);
}
function avm1_0x4C_ActionPushDuplicate(ectx) {
    var stack = ectx.stack;
    stack.push(stack[stack.length - 1]);
}
function avm1_0x3E_ActionReturn(ectx) {
    ectx.isEndOfActions = true;
}
function avm1_0x4D_ActionStackSwap(ectx) {
    var stack = ectx.stack;
    stack.push(stack.pop(), stack.pop());
}
function avm1_0x87_ActionStoreRegister(ectx, args) {
    var stack = ectx.stack;
    var registers = ectx.registers;
    var register = args[0];
    if (register < 0 || register >= registers.length) {
        return; // ignoring bad registers references
    }
    registers[register] = stack[stack.length - 1];
}
// SWF 6
function avm1_0x54_ActionInstanceOf(ectx) {
    var stack = ectx.stack;
    var constr = stack.pop();
    var obj = stack.pop();
    stack.push(as2InstanceOf(obj, constr));
}
function avm1_0x55_ActionEnumerate2(ectx) {
    var stack = ectx.stack;
    var obj = stack.pop();
    stack.push(null);
    // AVM1 just ignores lookups on non-existant containers
    if (isNullOrUndefined(obj)) {
        avm1Warn('AVM1 warning: cannot iterate over undefined object');
        return;
    }
    as2Enumerate(obj, function (name) {
        stack.push(name);
    }, null);
}
function avm1_0x66_ActionStrictEquals(ectx) {
    var stack = ectx.stack;
    var a = stack.pop();
    var b = stack.pop();
    stack.push(b === a);
}
function avm1_0x67_ActionGreater(ectx, args) {
    var stack = ectx.stack;
    var a = args ? args[1] : stack.pop();
    var b = args ? args[0] : stack.pop();
    return stack[stack.length] = (as2Compare(ectx.context, a, b));
}
function avm1_0x68_ActionStringGreater(ectx) {
    var stack = ectx.stack;
    var isSwfVersion5 = ectx.isSwfVersion5;
    var sa = alToString(ectx.context, stack.pop());
    var sb = alToString(ectx.context, stack.pop());
    var f = sb > sa;
    stack.push(isSwfVersion5 ? f : f ? 1 : 0);
}
// SWF 7
function avm1_0x8E_ActionDefineFunction2(ectx, args) {
    var stack = ectx.stack;
    var scope = ectx.scopeList.scope;
    var functionBody = args[0];
    var functionName = args[1];
    var functionParams = args[2];
    var registerCount = args[3];
    var registerAllocation = args[4];
    var suppressArguments = args[5];
    var fn = avm1DefineFunction(ectx, functionBody, functionName, functionParams, registerCount, registerAllocation, suppressArguments);
    if (functionName) {
        scope.alPut(functionName, fn);
        as2SyncEvents(ectx.context, functionName, scope);
    }
    else {
        stack.push(fn);
    }
}
function avm1_0x69_ActionExtends(ectx) {
    var stack = ectx.stack;
    var context = ectx.context;
    var constrSuper = alToObject(context, stack.pop());
    var constr = alToObject(context, stack.pop());
    var prototype = constr.alGetPrototypeProperty();
    var prototypeSuper = constrSuper.alGetPrototypeProperty();
    prototype.alPrototype = prototypeSuper;
    var desc = new AVM1PropertyDescriptor(64 /* DATA */ | 1 /* DONT_ENUM */, constrSuper);
    prototype.alSetOwnProperty('__constructor__', desc);
}
function avm1_0x2B_ActionCastOp(ectx) {
    var stack = ectx.stack;
    var obj = stack.pop();
    var constr = stack.pop();
    stack.push(as2InstanceOf(obj, constr) ? obj : null);
}
function avm1_0x2C_ActionImplementsOp(ectx) {
    var stack = ectx.stack;
    var constr = stack.pop();
    var count = +stack.pop();
    fixArgsCount(count, stack.length);
    var interfaces = [];
    for (var i = 0; i < count; i++) {
        interfaces.push(stack.pop());
    }
    constr._as2Interfaces = interfaces;
}
function avm1_0x8F_ActionTry(ectx, args) {
    var catchIsRegisterFlag = args[0];
    var catchTarget = args[1];
    var tryBody = args[2];
    var catchBlockFlag = args[3];
    var catchBody = args[4];
    var finallyBlockFlag = args[5];
    var finallyBody = args[6];
    avm1ProcessTry(ectx, catchIsRegisterFlag, finallyBlockFlag, catchBlockFlag, catchTarget, tryBody, catchBody, finallyBody);
}
function avm1_0x2A_ActionThrow(ectx) {
    var stack = ectx.stack;
    var obj = stack.pop();
    throw new AVM1Error(obj);
}
function avm1_0x2D_ActionFSCommand2(ectx) {
    var stack = ectx.stack;
    var args = avm1ReadFunctionArgs(stack);
    var sp = stack.length;
    stack.push(undefined);
    /* eslint-disable-next-line */
    var result = ectx.actions.fscommand.apply(ectx.actions, args);
    stack[sp] = result;
}
function avm1_0x89_ActionStrictMode(ectx, args) {
    // const mode: number = args[0];
}
/*
// legacy
function wrapAvm1Error(fn: Function): Function {
    return function avm1ErrorWrapper(executionContext: ExecutionContext, args: any[]) {
        let currentContext: AVM1ContextImpl;
        // try {
        fn(executionContext, args);

        executionContext.recoveringFromError = false;
        // } catch (e) {
        // 	// handling AVM1 errors
        // 	currentContext = executionContext.context;
        // 	e = as2CastError(e);
        // 	if (e instanceof AVM1CriticalError) {
        // 		throw e;
        // 	}
        // 	if (e instanceof AVM1Error) {
        // 		throw e;
        // 	}

        // 	//Telemetry.instance.reportTelemetry({topic: 'error', error: ErrorTypes.AVM1_ERROR});

        // 	if (!executionContext.recoveringFromError) {
        // 		if (currentContext.errorsIgnored++ >= MAX_AVM1_ERRORS_LIMIT) {
        // 			throw new AVM1CriticalError('long running script -- AVM1 errors limit is reached');
        // 		}
        // 		console.log(typeof e);
        // 		console.log(Object.getPrototypeOf(e));
        // 		console.log(Object.getPrototypeOf(Object.getPrototypeOf(e)));
        // 		console.error('AVM1 error: ' + e);
        // 		// REDUX
        // 		//var avm2 = Shumway.AVM2.Runtime.AVM2;
        // 		//avm2.instance.exceptions.push({source: 'avm1', message: e.message,
        // 		//  stack: e.stack});
        // 		//executionContext.recoveringFromError = true;
        // 	}
        // }
    };
}
*/
export function generateActionCalls() {
    var wrap = function (fn) { return fn; };
    /*
    // legacy
    if (!avm1ErrorsEnabled.value) {
        wrap = wrapAvm1Error;
    } else {
        wrap = function (fn: Function) {
            return fn;
        };
    }
    */
    return {
        ActionGotoFrame: wrap(avm1_0x81_ActionGotoFrame),
        ActionGetURL: wrap(avm1_0x83_ActionGetURL),
        ActionNextFrame: wrap(avm1_0x04_ActionNextFrame),
        ActionPreviousFrame: wrap(avm1_0x05_ActionPreviousFrame),
        ActionPlay: wrap(avm1_0x06_ActionPlay),
        ActionStop: wrap(avm1_0x07_ActionStop),
        ActionToggleQuality: wrap(avm1_0x08_ActionToggleQuality),
        ActionStopSounds: wrap(avm1_0x09_ActionStopSounds),
        ActionWaitForFrame: wrap(avm1_0x8A_ActionWaitForFrame),
        ActionSetTarget: wrap(avm1_0x8B_ActionSetTarget),
        ActionGoToLabel: wrap(avm1_0x8C_ActionGoToLabel),
        ActionPush: wrap(avm1_0x96_ActionPush),
        ActionPop: wrap(avm1_0x17_ActionPop),
        ActionAdd: wrap(avm1_0x0A_ActionAdd),
        ActionSubtract: wrap(avm1_0x0B_ActionSubtract),
        ActionMultiply: wrap(avm1_0x0C_ActionMultiply),
        ActionDivide: wrap(avm1_0x0D_ActionDivide),
        ActionEquals: wrap(avm1_0x0E_ActionEquals),
        ActionLess: wrap(avm1_0x0F_ActionLess),
        ActionAnd: wrap(avm1_0x10_ActionAnd),
        ActionOr: wrap(avm1_0x11_ActionOr),
        ActionNot: wrap(avm1_0x12_ActionNot),
        ActionStringEquals: wrap(avm1_0x13_ActionStringEquals),
        ActionStringLength: wrap(avm1_0x14_ActionStringLength),
        ActionMBStringLength: wrap(avm1_0x31_ActionMBStringLength),
        ActionStringAdd: wrap(avm1_0x21_ActionStringAdd),
        ActionStringExtract: wrap(avm1_0x15_ActionStringExtract),
        ActionMBStringExtract: wrap(avm1_0x35_ActionMBStringExtract),
        ActionStringLess: wrap(avm1_0x29_ActionStringLess),
        ActionToInteger: wrap(avm1_0x18_ActionToInteger),
        ActionCharToAscii: wrap(avm1_0x32_ActionCharToAscii),
        ActionMBCharToAscii: wrap(avm1_0x36_ActionMBCharToAscii),
        ActionAsciiToChar: wrap(avm1_0x33_ActionAsciiToChar),
        ActionMBAsciiToChar: wrap(avm1_0x37_ActionMBAsciiToChar),
        ActionJump: wrap(avm1_0x99_ActionJump),
        ActionIf: wrap(avm1_0x9D_ActionIf),
        ActionCall: wrap(avm1_0x9E_ActionCall),
        ActionGetVariable: wrap(avm1_0x1C_ActionGetVariable),
        ActionSetVariable: wrap(avm1_0x1D_ActionSetVariable),
        ActionGetURL2: wrap(avm1_0x9A_ActionGetURL2),
        ActionGotoFrame2: wrap(avm1_0x9F_ActionGotoFrame2),
        ActionSetTarget2: wrap(avm1_0x20_ActionSetTarget2),
        ActionGetProperty: wrap(avm1_0x22_ActionGetProperty),
        ActionSetProperty: wrap(avm1_0x23_ActionSetProperty),
        ActionCloneSprite: wrap(avm1_0x24_ActionCloneSprite),
        ActionRemoveSprite: wrap(avm1_0x25_ActionRemoveSprite),
        ActionStartDrag: wrap(avm1_0x27_ActionStartDrag),
        ActionEndDrag: wrap(avm1_0x28_ActionEndDrag),
        ActionWaitForFrame2: wrap(avm1_0x8D_ActionWaitForFrame2),
        ActionTrace: wrap(avm1_0x26_ActionTrace),
        ActionGetTime: wrap(avm1_0x34_ActionGetTime),
        ActionRandomNumber: wrap(avm1_0x30_ActionRandomNumber),
        ActionCallFunction: wrap(avm1_0x3D_ActionCallFunction),
        ActionCallMethod: wrap(avm1_0x52_ActionCallMethod),
        ActionConstantPool: wrap(avm1_0x88_ActionConstantPool),
        ActionDefineFunction: wrap(avm1_0x9B_ActionDefineFunction),
        ActionDefineLocal: wrap(avm1_0x3C_ActionDefineLocal),
        ActionDefineLocal2: wrap(avm1_0x41_ActionDefineLocal2),
        ActionDelete: wrap(avm1_0x3A_ActionDelete),
        ActionDelete2: wrap(avm1_0x3B_ActionDelete2),
        ActionEnumerate: wrap(avm1_0x46_ActionEnumerate),
        ActionEquals2: wrap(avm1_0x49_ActionEquals2),
        ActionGetMember: wrap(avm1_0x4E_ActionGetMember),
        ActionInitArray: wrap(avm1_0x42_ActionInitArray),
        ActionInitObject: wrap(avm1_0x43_ActionInitObject),
        ActionNewMethod: wrap(avm1_0x53_ActionNewMethod),
        ActionNewObject: wrap(avm1_0x40_ActionNewObject),
        ActionSetMember: wrap(avm1_0x4F_ActionSetMember),
        ActionTargetPath: wrap(avm1_0x45_ActionTargetPath),
        ActionWith: wrap(avm1_0x94_ActionWith),
        ActionToNumber: wrap(avm1_0x4A_ActionToNumber),
        ActionToString: wrap(avm1_0x4B_ActionToString),
        ActionTypeOf: wrap(avm1_0x44_ActionTypeOf),
        ActionAdd2: wrap(avm1_0x47_ActionAdd2),
        ActionLess2: wrap(avm1_0x48_ActionLess2),
        ActionModulo: wrap(avm1_0x3F_ActionModulo),
        ActionBitAnd: wrap(avm1_0x60_ActionBitAnd),
        ActionBitLShift: wrap(avm1_0x63_ActionBitLShift),
        ActionBitOr: wrap(avm1_0x61_ActionBitOr),
        ActionBitRShift: wrap(avm1_0x64_ActionBitRShift),
        ActionBitURShift: wrap(avm1_0x65_ActionBitURShift),
        ActionBitXor: wrap(avm1_0x62_ActionBitXor),
        ActionDecrement: wrap(avm1_0x51_ActionDecrement),
        ActionIncrement: wrap(avm1_0x50_ActionIncrement),
        ActionPushDuplicate: wrap(avm1_0x4C_ActionPushDuplicate),
        ActionReturn: wrap(avm1_0x3E_ActionReturn),
        ActionStackSwap: wrap(avm1_0x4D_ActionStackSwap),
        ActionStoreRegister: wrap(avm1_0x87_ActionStoreRegister),
        ActionInstanceOf: wrap(avm1_0x54_ActionInstanceOf),
        ActionEnumerate2: wrap(avm1_0x55_ActionEnumerate2),
        ActionStrictEquals: wrap(avm1_0x66_ActionStrictEquals),
        ActionGreater: wrap(avm1_0x67_ActionGreater),
        ActionStringGreater: wrap(avm1_0x68_ActionStringGreater),
        ActionDefineFunction2: wrap(avm1_0x8E_ActionDefineFunction2),
        ActionExtends: wrap(avm1_0x69_ActionExtends),
        ActionCastOp: wrap(avm1_0x2B_ActionCastOp),
        ActionImplementsOp: wrap(avm1_0x2C_ActionImplementsOp),
        ActionTry: wrap(avm1_0x8F_ActionTry),
        ActionThrow: wrap(avm1_0x2A_ActionThrow),
        ActionFSCommand2: wrap(avm1_0x2D_ActionFSCommand2),
        ActionStrictMode: wrap(avm1_0x89_ActionStrictMode)
    };
}
function interpretAction(executionContext, parsedAction) {
    var stack = executionContext.stack;
    var actionCode = parsedAction.actionCode;
    var args = parsedAction.args;
    var actionTracer = executionContext.context.actionTracer;
    release || (actionTracer && actionTracer.print(parsedAction, stack));
    var shallBranch = false;
    switch (actionCode | 0) {
        // SWF 3 actions
        case 129 /* ActionGotoFrame */:
            avm1_0x81_ActionGotoFrame(executionContext, args);
            break;
        case 131 /* ActionGetURL */:
            avm1_0x83_ActionGetURL(executionContext, args);
            break;
        case 4 /* ActionNextFrame */:
            avm1_0x04_ActionNextFrame(executionContext);
            break;
        case 5 /* ActionPreviousFrame */:
            avm1_0x05_ActionPreviousFrame(executionContext);
            break;
        case 6 /* ActionPlay */:
            avm1_0x06_ActionPlay(executionContext);
            break;
        case 7 /* ActionStop */:
            avm1_0x07_ActionStop(executionContext);
            break;
        case 8 /* ActionToggleQuality */:
            avm1_0x08_ActionToggleQuality(executionContext);
            break;
        case 9 /* ActionStopSounds */:
            avm1_0x09_ActionStopSounds(executionContext);
            break;
        case 138 /* ActionWaitForFrame */:
            shallBranch = avm1_0x8A_ActionWaitForFrame(executionContext, args);
            break;
        case 139 /* ActionSetTarget */:
            avm1_0x8B_ActionSetTarget(executionContext, args);
            break;
        case 140 /* ActionGoToLabel */:
            avm1_0x8C_ActionGoToLabel(executionContext, args);
            break;
        // SWF 4 actions
        case 150 /* ActionPush */:
            avm1_0x96_ActionPush(executionContext, args);
            break;
        case 23 /* ActionPop */:
            avm1_0x17_ActionPop(executionContext);
            break;
        case 10 /* ActionAdd */:
            avm1_0x0A_ActionAdd(executionContext);
            break;
        case 11 /* ActionSubtract */:
            avm1_0x0B_ActionSubtract(executionContext);
            break;
        case 12 /* ActionMultiply */:
            avm1_0x0C_ActionMultiply(executionContext);
            break;
        case 13 /* ActionDivide */:
            avm1_0x0D_ActionDivide(executionContext);
            break;
        case 14 /* ActionEquals */:
            avm1_0x0E_ActionEquals(executionContext);
            break;
        case 15 /* ActionLess */:
            avm1_0x0F_ActionLess(executionContext);
            break;
        case 16 /* ActionAnd */:
            avm1_0x10_ActionAnd(executionContext);
            break;
        case 17 /* ActionOr */:
            avm1_0x11_ActionOr(executionContext);
            break;
        case 18 /* ActionNot */:
            avm1_0x12_ActionNot(executionContext);
            break;
        case 19 /* ActionStringEquals */:
            avm1_0x13_ActionStringEquals(executionContext);
            break;
        case 20 /* ActionStringLength */:
            avm1_0x14_ActionStringLength(executionContext);
            break;
        case 49 /* ActionMBStringLength */:
            avm1_0x31_ActionMBStringLength(executionContext);
            break;
        case 33 /* ActionStringAdd */:
            avm1_0x21_ActionStringAdd(executionContext);
            break;
        case 21 /* ActionStringExtract */:
            avm1_0x15_ActionStringExtract(executionContext);
            break;
        case 53 /* ActionMBStringExtract */:
            avm1_0x35_ActionMBStringExtract(executionContext);
            break;
        case 41 /* ActionStringLess */:
            avm1_0x29_ActionStringLess(executionContext);
            break;
        case 24 /* ActionToInteger */:
            avm1_0x18_ActionToInteger(executionContext);
            break;
        case 50 /* ActionCharToAscii */:
            avm1_0x32_ActionCharToAscii(executionContext);
            break;
        case 54 /* ActionMBCharToAscii */:
            avm1_0x36_ActionMBCharToAscii(executionContext);
            break;
        case 51 /* ActionAsciiToChar */:
            avm1_0x33_ActionAsciiToChar(executionContext);
            break;
        case 55 /* ActionMBAsciiToChar */:
            avm1_0x37_ActionMBAsciiToChar(executionContext);
            break;
        case 153 /* ActionJump */:
            avm1_0x99_ActionJump(executionContext, args);
            break;
        case 157 /* ActionIf */:
            shallBranch = avm1_0x9D_ActionIf(executionContext, args);
            break;
        case 158 /* ActionCall */:
            avm1_0x9E_ActionCall(executionContext);
            break;
        case 28 /* ActionGetVariable */:
            avm1_0x1C_ActionGetVariable(executionContext);
            break;
        case 29 /* ActionSetVariable */:
            avm1_0x1D_ActionSetVariable(executionContext);
            break;
        case 154 /* ActionGetURL2 */:
            avm1_0x9A_ActionGetURL2(executionContext, args);
            break;
        case 159 /* ActionGotoFrame2 */:
            avm1_0x9F_ActionGotoFrame2(executionContext, args);
            break;
        case 32 /* ActionSetTarget2 */:
            avm1_0x20_ActionSetTarget2(executionContext);
            break;
        case 34 /* ActionGetProperty */:
            avm1_0x22_ActionGetProperty(executionContext);
            break;
        case 35 /* ActionSetProperty */:
            avm1_0x23_ActionSetProperty(executionContext);
            break;
        case 36 /* ActionCloneSprite */:
            avm1_0x24_ActionCloneSprite(executionContext);
            break;
        case 37 /* ActionRemoveSprite */:
            avm1_0x25_ActionRemoveSprite(executionContext);
            break;
        case 39 /* ActionStartDrag */:
            avm1_0x27_ActionStartDrag(executionContext);
            break;
        case 40 /* ActionEndDrag */:
            avm1_0x28_ActionEndDrag(executionContext);
            break;
        case 141 /* ActionWaitForFrame2 */:
            shallBranch = avm1_0x8D_ActionWaitForFrame2(executionContext, args);
            break;
        case 38 /* ActionTrace */:
            avm1_0x26_ActionTrace(executionContext);
            break;
        case 52 /* ActionGetTime */:
            avm1_0x34_ActionGetTime(executionContext);
            break;
        case 48 /* ActionRandomNumber */:
            avm1_0x30_ActionRandomNumber(executionContext);
            break;
        // SWF 5
        case 61 /* ActionCallFunction */:
            avm1_0x3D_ActionCallFunction(executionContext);
            break;
        case 82 /* ActionCallMethod */:
            avm1_0x52_ActionCallMethod(executionContext);
            break;
        case 136 /* ActionConstantPool */:
            avm1_0x88_ActionConstantPool(executionContext, args);
            break;
        case 155 /* ActionDefineFunction */:
            avm1_0x9B_ActionDefineFunction(executionContext, args);
            break;
        case 60 /* ActionDefineLocal */:
            avm1_0x3C_ActionDefineLocal(executionContext);
            break;
        case 65 /* ActionDefineLocal2 */:
            avm1_0x41_ActionDefineLocal2(executionContext);
            break;
        case 58 /* ActionDelete */:
            avm1_0x3A_ActionDelete(executionContext);
            break;
        case 59 /* ActionDelete2 */:
            avm1_0x3B_ActionDelete2(executionContext);
            break;
        case 70 /* ActionEnumerate */:
            avm1_0x46_ActionEnumerate(executionContext);
            break;
        case 73 /* ActionEquals2 */:
            avm1_0x49_ActionEquals2(executionContext);
            break;
        case 78 /* ActionGetMember */:
            avm1_0x4E_ActionGetMember(executionContext);
            break;
        case 66 /* ActionInitArray */:
            avm1_0x42_ActionInitArray(executionContext);
            break;
        case 67 /* ActionInitObject */:
            avm1_0x43_ActionInitObject(executionContext);
            break;
        case 83 /* ActionNewMethod */:
            avm1_0x53_ActionNewMethod(executionContext);
            break;
        case 64 /* ActionNewObject */:
            avm1_0x40_ActionNewObject(executionContext);
            break;
        case 79 /* ActionSetMember */:
            avm1_0x4F_ActionSetMember(executionContext);
            break;
        case 69 /* ActionTargetPath */:
            avm1_0x45_ActionTargetPath(executionContext);
            break;
        case 148 /* ActionWith */:
            avm1_0x94_ActionWith(executionContext, args);
            break;
        case 74 /* ActionToNumber */:
            avm1_0x4A_ActionToNumber(executionContext);
            break;
        case 75 /* ActionToString */:
            avm1_0x4B_ActionToString(executionContext);
            break;
        case 68 /* ActionTypeOf */:
            avm1_0x44_ActionTypeOf(executionContext);
            break;
        case 71 /* ActionAdd2 */:
            avm1_0x47_ActionAdd2(executionContext);
            break;
        case 72 /* ActionLess2 */:
            avm1_0x48_ActionLess2(executionContext);
            break;
        case 63 /* ActionModulo */:
            avm1_0x3F_ActionModulo(executionContext);
            break;
        case 96 /* ActionBitAnd */:
            avm1_0x60_ActionBitAnd(executionContext);
            break;
        case 99 /* ActionBitLShift */:
            avm1_0x63_ActionBitLShift(executionContext);
            break;
        case 97 /* ActionBitOr */:
            avm1_0x61_ActionBitOr(executionContext);
            break;
        case 100 /* ActionBitRShift */:
            avm1_0x64_ActionBitRShift(executionContext);
            break;
        case 101 /* ActionBitURShift */:
            avm1_0x65_ActionBitURShift(executionContext);
            break;
        case 98 /* ActionBitXor */:
            avm1_0x62_ActionBitXor(executionContext);
            break;
        case 81 /* ActionDecrement */:
            avm1_0x51_ActionDecrement(executionContext);
            break;
        case 80 /* ActionIncrement */:
            avm1_0x50_ActionIncrement(executionContext);
            break;
        case 76 /* ActionPushDuplicate */:
            avm1_0x4C_ActionPushDuplicate(executionContext);
            break;
        case 62 /* ActionReturn */:
            avm1_0x3E_ActionReturn(executionContext);
            break;
        case 77 /* ActionStackSwap */:
            avm1_0x4D_ActionStackSwap(executionContext);
            break;
        case 135 /* ActionStoreRegister */:
            avm1_0x87_ActionStoreRegister(executionContext, args);
            break;
        // SWF 6
        case 84 /* ActionInstanceOf */:
            avm1_0x54_ActionInstanceOf(executionContext);
            break;
        case 85 /* ActionEnumerate2 */:
            avm1_0x55_ActionEnumerate2(executionContext);
            break;
        case 102 /* ActionStrictEquals */:
            avm1_0x66_ActionStrictEquals(executionContext);
            break;
        case 103 /* ActionGreater */:
            avm1_0x67_ActionGreater(executionContext);
            break;
        case 104 /* ActionStringGreater */:
            avm1_0x68_ActionStringGreater(executionContext);
            break;
        // SWF 7
        case 142 /* ActionDefineFunction2 */:
            avm1_0x8E_ActionDefineFunction2(executionContext, args);
            break;
        case 105 /* ActionExtends */:
            avm1_0x69_ActionExtends(executionContext);
            break;
        case 43 /* ActionCastOp */:
            avm1_0x2B_ActionCastOp(executionContext);
            break;
        case 44 /* ActionImplementsOp */:
            avm1_0x2C_ActionImplementsOp(executionContext);
            break;
        case 143 /* ActionTry */:
            avm1_0x8F_ActionTry(executionContext, args);
            break;
        case 42 /* ActionThrow */:
            avm1_0x2A_ActionThrow(executionContext);
            break;
        // Not documented by the spec
        case 45 /* ActionFSCommand2 */:
            avm1_0x2D_ActionFSCommand2(executionContext);
            break;
        case 137 /* ActionStrictMode */:
            avm1_0x89_ActionStrictMode(executionContext, args);
            break;
        case 0 /* None */: // End of actions
            executionContext.isEndOfActions = true;
            break;
        default:
            throw new Error('Unknown action code: ' + actionCode);
    }
    return shallBranch;
}
function interpretActionWithRecovery(executionContext, parsedAction) {
    var currentContext;
    var result;
    try {
        result = interpretAction(executionContext, parsedAction);
        executionContext.recoveringFromError = false;
    }
    catch (e) {
        // handling AVM1 errors
        currentContext = executionContext.context;
        var ce = as2CastError(e);
        if ((avm1ErrorsEnabled.value && !currentContext.isTryCatchListening) ||
            ce instanceof AVM1CriticalError) {
            throw ce;
        }
        if (ce instanceof AVM1Error) {
            throw ce;
        }
        Telemetry.instance.reportTelemetry({ topic: 'error', error: 1 /* AVM1_ERROR */ });
        if (!executionContext.recoveringFromError) {
            if (currentContext.errorsIgnored++ >= MAX_AVM1_ERRORS_LIMIT) {
                throw new AVM1CriticalError('long running script -- AVM1 errors limit is reached');
            }
            console.error('AVM1 error: ' + e);
            // REDUX
            //var avm2 = Shumway.AVM2.Runtime.AVM2;
            //avm2.instance.exceptions.push({source: 'avm1', message: e.message,
            //  stack: e.stack});
            executionContext.recoveringFromError = true;
        }
    }
    return result;
}
function analyzeAndCompileActionsData(ectx, actionsData) {
    var context = ectx.context;
    var compiled;
    /*if (avm1WellknownActionsCompilationsEnabled.value) {
        compiled = findWellknowCompilation(actionsData, context);
        if (compiled) {
            actionsData.compiled = compiled;
            return;
        }
    }*/
    var parser = new ActionsDataParser(actionsData, context.swfVersion);
    var analyzer = new ActionsDataAnalyzer();
    analyzer.registersLimit = ectx.registers.length;
    analyzer.parentResults = actionsData.parent && actionsData.parent.ir;
    var ir = analyzer.analyze(parser);
    actionsData.ir = ir;
    if (avm1CompilerEnabled.value) {
        //try {
        var c = new ActionsDataCompiler();
        compiled = c.generate(ir, actionsData.debugPath);
        actionsData.compiled = compiled;
        //} catch (e) {
        //	console.error('Unable to compile AVM1 function: ' + e);
        //}
    }
}
function interpretActionsData(ectx, actionsData) {
    if (!actionsData.ir && !actionsData.compiled) {
        analyzeAndCompileActionsData(ectx, actionsData);
    }
    var currentContext = ectx.context;
    var scopeList = ectx.scopeList;
    var scope = scopeList.scope;
    var awayObject = scope.adaptee; // FIXME refactor
    if (awayObject && awayObject._deferScriptExecution) {
        currentContext.deferScriptExecution = true;
    }
    if (awayObject) {
        AVM1MovieClip.currentMCAssetNameSpace = awayObject.assetNamespace;
    }
    var compiled = actionsData.compiled;
    if (compiled) {
        release || (currentContext.actionTracer && currentContext.actionTracer.message('Running compiled ' + actionsData.id));
        return compiled(ectx);
    }
    var instructionsExecuted = 0;
    var abortExecutionAt = currentContext.abortExecutionAt;
    var ir = actionsData.ir;
    /*
    // legacy, deopt
    if (avm1DebuggerEnabled.value &&
        (Debugger.pause || Debugger.breakpoints[(<AnalyzerResults>ir).dataId])) {
        debugger;
    }
    */
    release || Debug.assert(ir);
    var position = 0;
    var nextAction = ir.actions[position];
    // will try again if we are skipping errors
    while (nextAction && !ectx.isEndOfActions) {
        // let's check timeout/Date.now every some number of instructions
        if (instructionsExecuted++ % CHECK_AVM1_HANG_EVERY === 0 && Date.now() >= abortExecutionAt) {
            throw new AVM1CriticalError('long running script -- AVM1 instruction hang timeout');
        }
        var shallBranch = interpretActionWithRecovery(ectx, nextAction.action);
        if (shallBranch) {
            position = nextAction.conditionalJumpTo;
        }
        else {
            position = nextAction.next;
        }
        nextAction = ir.actions[position];
    }
    return ectx.stack.pop();
}
var ActionTracer = /** @class */ (function () {
    function ActionTracer() {
        this._indentation = 0;
        this._indentStringCache = [];
    }
    ActionTracer.prototype._getIndentString = function () {
        return this._indentStringCache[this._indentation] ||
            (this._indentStringCache[this._indentation] = new Array(this._indentation + 1).join('..'));
    };
    ActionTracer.prototype.print = function (parsedAction, stack) {
        var position = parsedAction.position;
        var actionCode = parsedAction.actionCode;
        var actionName = parsedAction.actionName;
        var stackDump = [];
        for (var q = 0; q < stack.length; q++) {
            var item = stack[q];
            if (item && typeof item === 'object') {
                var constr = item.alGetConstructorProperty();
                stackDump.push('[' + (constr ? constr.name : 'Object') + ']');
            }
            else {
                stackDump.push(item);
            }
        }
        console.log('AVM1 trace: ' + this._getIndentString() + position + ': ' +
            actionName + '(' + actionCode.toString(16) + '), ' +
            'stack=' + stackDump);
    };
    ActionTracer.prototype.indent = function () {
        this._indentation++;
    };
    ActionTracer.prototype.unindent = function () {
        this._indentation--;
    };
    ActionTracer.prototype.message = function (msg) {
        console.log('AVM1 trace: ------- ' + msg);
    };
    return ActionTracer;
}());
export { ActionTracer };
